import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SyncIcon from "@mui/icons-material/Sync";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import PanoramaHorizontalIcon from "@mui/icons-material/PanoramaHorizontal";
import ViewCarouselSharpIcon from "@mui/icons-material/ViewCarouselSharp";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import { PatrolLivePtzControls } from "./PatrolLivePtzControls.tsx";
import "./patrolLivePtzPip.css";
import { resolvePatrolMediaUrl } from "./patrolMediaUrls";
import * as THREE from "three";
import PatrolImageViewer from "./PatrolImageViewer";
import { PatrolLiveMonitoringEmptyState } from "./PatrolLiveMonitoringEmptyState";
import { THEME } from "../../../constants/patrolFeatureTab";
import PanoramaHorizontalSelectIcon from "@mui/icons-material/PanoramaHorizontalSelect";
import ThreeDRotationIcon from "@mui/icons-material/ThreeDRotation";
import {
  PictureInPictureIcon,
  PictureInMaxPictureIcon,
  PictureInPictureCircleIcon,
  CircularProgressIcon,
} from "./Patrol360Icons";
import { PatrolViewerMetaStrip } from "./PatrolViewerMetaStrip";
import {
  DEFAULT_PATROL_ROTATION_DURATION_SECONDS,
  patrolRotationDurationMs,
} from "./patrolRotationSpeed";
import {
  computePatrolPtzPreviewPx,
  normalizePatrolPtzPreviewSharePct,
  PATROL_PTZ_PREVIEW_SHARE_DEFAULT,
} from "./patrolPtzPreviewSize";
import {
  PATROL_SAMPLE_TOUR_TRANSITION_MS,
  animatePatrolMarzipanoSphereCrossfade,
  patrolSampleTourBlendDown,
  patrolSampleTourBlendUp,
} from "./patrolSceneCrossfade.js";
import { PatrolLinkHotspot } from "./PatrolLinkHotspot";
import { PatrolAssetHotspotsLayer } from "./PatrolAssetHotspotsLayer";
import {
  getPatrolAssetHotspotsForRecording,
  hasPatrolAssetHotspots,
} from "./patrolAssetHotspots";
import { PATROL_HOTSPOT_LAYER_TOGGLE_ICON } from "./patrolHotspotIcons.js";
import {
  bearingDegForPatrolSegment,
  normalizePatrolFloorPins,
  nextPatrolPinIndex,
  patrolLinkHotspotYawDeg,
  patrolVideoIndexForFloorPin,
} from "./patrolFloorPins";
import Hls from "hls.js";
import { resolveOmniMediaUrl } from "../../../api/client.ts";
import {
  DEFAULT_LIVE_STREAM_TRANSPORT,
  resolveRelayStreamUrl,
} from "../../../api/cameras.ts";
import { attachLivePlayback } from "../../../lib/live-playback.ts";
import {
  getPipPopupHidden,
  setPipPopupHidden,
} from "../../../lib/previewSession.ts";
import CircularProgress from "@mui/material/CircularProgress";
import { StreamVideo } from "../../../components/place360/StreamVideo.tsx";
import { PatrolPip360LivePreview } from "./PatrolPip360LivePreview.jsx";
import {
  createPatrolEquirectSphereMesh,
  createPatrolEquirectVideoTexture,
} from "./patrolEquirectRenderer.js";
import { samplePatrolEquirectNormFromClient } from "./patrolEquirectPick.js";
import { pickNearestMappedPresetGridId } from "./patrolPresetGridPick.js";
import { usePatrolLivePtzAim } from "./usePatrolLivePtzAim.ts";
import { PatrolPanoramaFocusRing } from "./PatrolPanoramaFocusRing.jsx";
import { PatrolPresetGridOverlay } from "./PatrolPresetGridOverlay.jsx";

/** Resolve patrol live HLS playlist for the active recording row. */
function patrolHlsUrlFromRecording(rec) {
  if (!rec || typeof rec !== "object") return null;
  const candidates = [
    rec.live_hls_url,
    rec.live_stream_kind === "ptz"
      ? rec.live_hls_ptz_url
      : rec.live_hls_360_url,
    rec.live_hls_ptz_url,
    rec.live_hls_360_url,
    rec.recording_path,
  ];
  for (const url of candidates) {
    const s = String(url ?? "").trim();
    if (s && /\.m3u8(\?|$)/i.test(s)) return s;
  }
  return null;
}

/** Relay URLs from a live patrol recording row (HLS + WebRTC). */
function patrolLiveRelayFromRecording(rec) {
  if (!rec || typeof rec !== "object") {
    return { hls360: null, hlsPtz: null, webrtc360: null, webrtcPtz: null };
  }
  const hls360Direct = String(rec.live_hls_360_url ?? "").trim();
  const hls360 =
    hls360Direct && /\.m3u8(\?|$)/i.test(hls360Direct) ? hls360Direct : null;
  const hlsPtzRaw = String(rec.live_hls_ptz_url ?? "").trim();
  const hlsPtz = /\.m3u8(\?|$)/i.test(hlsPtzRaw) ? hlsPtzRaw : null;
  const primary = String(rec.live_hls_url ?? "").trim();
  const hls360Fallback =
    primary && /\.m3u8(\?|$)/i.test(primary) ? primary : null;
  return {
    hls360: hls360 || hls360Fallback,
    hlsPtz,
    webrtc360: String(rec.live_webrtc_360_url ?? "").trim() || null,
    webrtcPtz: String(rec.live_webrtc_ptz_url ?? "").trim() || null,
  };
}

/** FOV lerp per frame (~60fps) — smooth wheel zoom (~300ms). */
const PATROL_FOV_LERP_FACTOR = 0.14;
const PATROL_FOV_MIN = 20;
const PATROL_FOV_MAX = 120;
const PATROL_WHEEL_ZOOM_STEP = 4;
/** Horizontal wheel / trackpad pan — scroll right pans view right. */
const PATROL_WHEEL_PAN_STEP = 0.12;
/** Max pointer movement (px) treated as click-to-aim, not drag-rotate (wankhede-360). */
const PATROL_CLICK_MOVE_PX = 10;
const PATROL_EQUIRECT_ASSET_HOTSPOTS_STORAGE_KEY =
  "patrolShowEquirectAssetHotspots";

/** Default ON unless user explicitly saved "false" in localStorage. */
function readPatrolAssetHotspotsPreference() {
  try {
    const stored = localStorage.getItem(PATROL_EQUIRECT_ASSET_HOTSPOTS_STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    /* ignore */
  }
  return true;
}

/**
 * PatrolVideoPlayer Component
 *
 * A video player component for sequential patrol videos with support for:
 * - Play/pause controls
 * - Previous/next video navigation
 * - Flat and 360-degree viewing modes
 * - Mouse rotation for 360-degree videos
 *
 * @param {Array<string>} videos - Array of video URLs to play sequentially
 * @param {number} initialVideoIndex - Initial video index to start playing (default: 0)
 * @param {boolean} autoPlay - Whether to auto-play video when loaded (default: false)
 * @param {Function} onSettingsClick - Callback function when settings icon is clicked
 * @param {string} imageUrl - URL of the floor plan image to display
 * @param {Array} pins - Array of pin data for the floor plan
 * @param {Array} selectedPatrol - Array of patrol objects with pin_id to map videos to pins
 * @param {number} leftPanelWidth - Width of the left panel for responsive layout
 * @param {boolean} showLiveMonitoringFloorControls - When true (Digital Patrol live tab only), extra floor-plan chrome: fullscreen in expanded toolbar, maximize chip without requiring browser fullscreen.
 */
export function Patrol360Player({
  currentVideoIndex,
  setCurrentVideoIndex,
  autoPlay = false,
  rotationDurationSeconds = DEFAULT_PATROL_ROTATION_DURATION_SECONDS,
  imageUrl = null,
  selectedPatrol = [],
  showLiveMonitoringFloorControls = false,
  liveMonitoring = false,
  liveHlsUrl = null,
  liveMonitoringLoading = false,
  ptzPreviewSharePct = PATROL_PTZ_PREVIEW_SHARE_DEFAULT,
  /** Live dual-camera toolbar props for PatrolViewer header (wankhede-360). */
  onLivePreviewToolbarChange,
}) {
  const liveHlsRef = useRef(null);

  const videos = useMemo(() => {
    if (!Array.isArray(selectedPatrol)) return [];
    if (liveMonitoring) {
      return selectedPatrol.map((patrol) => patrolHlsUrlFromRecording(patrol) || "");
    }
    return selectedPatrol.map((patrol) =>
      resolvePatrolMediaUrl(patrol.recording_path),
    );
  }, [selectedPatrol, liveMonitoring]);

  const liveActiveRecording = useMemo(() => {
    if (!liveMonitoring || !Array.isArray(selectedPatrol)) return null;
    return selectedPatrol[currentVideoIndex] ?? null;
  }, [liveMonitoring, selectedPatrol, currentVideoIndex]);

  const isPatrolDualTypeCamera =
    liveActiveRecording?.camera_type === "360° + PTZ";

  const patrolLiveStreamKind = useMemo(() => {
    if (!liveActiveRecording) return "360";
    if (isPatrolDualTypeCamera) return "360";
    const kind = String(liveActiveRecording.live_stream_kind ?? "").trim();
    if (kind === "ptz" || kind === "360") return kind;
    if (liveActiveRecording.live_hls_ptz_url && !liveActiveRecording.live_hls_360_url) {
      return "ptz";
    }
    return "360";
  }, [liveActiveRecording, isPatrolDualTypeCamera]);

  const liveRelayUrls = useMemo(
    () => patrolLiveRelayFromRecording(liveActiveRecording),
    [liveActiveRecording],
  );

  const [stream360Transport, setStream360Transport] = useState(
    DEFAULT_LIVE_STREAM_TRANSPORT,
  );
  const [streamPtzTransport, setStreamPtzTransport] = useState(
    DEFAULT_LIVE_STREAM_TRANSPORT,
  );

  const live360StreamUrl = useMemo(
    () =>
      liveActiveRecording
        ? resolveRelayStreamUrl(liveRelayUrls, "360", stream360Transport)
        : null,
    [liveActiveRecording, liveRelayUrls, stream360Transport],
  );

  const livePtzStreamUrl = useMemo(
    () =>
      liveActiveRecording
        ? resolveRelayStreamUrl(liveRelayUrls, "ptz", streamPtzTransport)
        : null,
    [liveActiveRecording, liveRelayUrls, streamPtzTransport],
  );

  const patrolDualStream =
    liveMonitoring &&
    Boolean(live360StreamUrl) &&
    Boolean(livePtzStreamUrl);

  const livePtzCameraId = useMemo(() => {
    const id = liveActiveRecording?.camera_id ?? liveActiveRecording?.pin_id;
    return id ? String(id) : "";
  }, [liveActiveRecording]);

  const isLivePtzOnlyCamera =
    liveMonitoring &&
    patrolLiveStreamKind === "ptz" &&
    !isPatrolDualTypeCamera;

  /** 360° + PTZ on same camera — tap PiP to swap main / preview (wankhede-360). */
  const patrolCanLensSwap = useMemo(
    () =>
      liveMonitoring &&
      Boolean(live360StreamUrl) &&
      Boolean(livePtzStreamUrl),
    [liveMonitoring, live360StreamUrl, livePtzStreamUrl],
  );

  const [patrolMainLens, setPatrolMainLens] = useState("360");
  const [patrolPipPopupHidden, setPatrolPipPopupHiddenState] = useState(() =>
    getPipPopupHidden(),
  );

  useEffect(() => {
    setPatrolMainLens("360");
  }, [currentVideoIndex, live360StreamUrl, livePtzStreamUrl]);

  /** PiP hide preference only when the patrol pin changes — not on HLS URL refresh. */
  useEffect(() => {
    setPatrolPipPopupHiddenState(getPipPopupHidden());
  }, [currentVideoIndex]);

  const revealPatrolLivePtzPip = useCallback(() => {
    setPatrolPipPopupHiddenState(false);
    setPipPopupHidden(false);
  }, []);

  /** True when the event target is a PTZ control (not the PiP swap tap surface). */
  const isPatrolPipControlTarget = useCallback((target) => {
    const el = target;
    if (!el || typeof el.closest !== "function") return false;
    if (el.closest(".patrol-pip-swap-layer, .patrol-pip-swap-hit")) return true;
    return Boolean(
      el.closest(
        ".ds-ptz-dpad, .ds-ptz-btn, .ds-ptz-edge-pad__btn, .ds-ptz-zoom-panel__btn, .ds-ptz-zoom-panel__reset, .ds-ptz-zoom-panel__slider-wrap, .ds-preview-ptz-fs-overlay, .ds-preview-ptz-fs-zoom, button:not(.patrol-pip-swap-layer):not(.patrol-pip-swap-hit), input, [data-pip-drag-handle]",
      ),
    );
  }, []);

  const patrolLiveMainStreamUrl = useMemo(() => {
    if (!liveMonitoring) return liveHlsUrl ?? null;
    if (patrolCanLensSwap) {
      return patrolMainLens === "ptz" ? livePtzStreamUrl : live360StreamUrl;
    }
    if (isLivePtzOnlyCamera && livePtzStreamUrl) return livePtzStreamUrl;
    if (live360StreamUrl) return live360StreamUrl;
    if (isPatrolDualTypeCamera) return null;
    if (liveHlsUrl) return liveHlsUrl;
    return null;
  }, [
    liveMonitoring,
    patrolCanLensSwap,
    patrolMainLens,
    livePtzStreamUrl,
    live360StreamUrl,
    isLivePtzOnlyCamera,
    liveHlsUrl,
    isPatrolDualTypeCamera,
  ]);

  const patrolLiveMainStreamTransport =
    patrolMainLens === "ptz" ? streamPtzTransport : stream360Transport;

  const patrolLivePipStreamUrl = useMemo(() => {
    if (!liveMonitoring) return null;
    if (patrolCanLensSwap) {
      return patrolMainLens === "360" ? livePtzStreamUrl : live360StreamUrl;
    }
    return livePtzStreamUrl;
  }, [
    liveMonitoring,
    patrolCanLensSwap,
    patrolMainLens,
    livePtzStreamUrl,
    live360StreamUrl,
  ]);

  const patrolLivePipStreamTransport =
    patrolMainLens === "360" ? streamPtzTransport : stream360Transport;

  const isPatrolLivePtzFlatMain = Boolean(
    liveMonitoring &&
      patrolLiveMainStreamUrl &&
      (isLivePtzOnlyCamera ||
        (patrolCanLensSwap && patrolMainLens === "ptz")),
  );

  const isPatrolLive360Main = Boolean(
    liveMonitoring &&
      !isPatrolLivePtzFlatMain &&
      Boolean(patrolLiveMainStreamUrl) &&
      (patrolCanLensSwap ? patrolMainLens === "360" : !isLivePtzOnlyCamera),
  );

  const showPatrolPtzControlsOnMain =
    isPatrolLivePtzFlatMain && Boolean(livePtzCameraId);

  const patrolLivePtzAimEnabled =
    liveMonitoring && Boolean(livePtzCameraId);
  const patrolLivePtzAim = usePatrolLivePtzAim(livePtzCameraId, patrolLivePtzAimEnabled, {
    vendor: liveActiveRecording?.camera_vendor,
  });

  const onPatrolPresetGridSelect = useCallback(
    (gridId) => {
      patrolLivePtzAim.handlePresetGridSelect(gridId);
      revealPatrolLivePtzPip();
    },
    [patrolLivePtzAim.handlePresetGridSelect, revealPatrolLivePtzPip],
  );

  const patrolPipLensLabel = patrolMainLens === "360" ? "PTZ" : "360°";

  /** Patrol prev/rotate/next on main toolbar (not on small PiP). */
  const showPatrolPlayerChromeInToolbar =
    !patrolCanLensSwap ||
    patrolMainLens === "360" ||
    patrolMainLens === "ptz";
  const showPatrolAutoRotateInToolbar = showPatrolPlayerChromeInToolbar;

  const patrolPtzSharePct = useMemo(
    () => normalizePatrolPtzPreviewSharePct(ptzPreviewSharePct),
    [ptzPreviewSharePct],
  );

  const [patrolVideoStageRect, setPatrolVideoStageRect] = useState({
    width: 0,
    height: 0,
  });

  const patrolPtzPreviewPx = useMemo(
    () =>
      computePatrolPtzPreviewPx(
        patrolVideoStageRect.width,
        patrolVideoStageRect.height,
        patrolPtzSharePct,
      ),
    [patrolVideoStageRect.width, patrolVideoStageRect.height, patrolPtzSharePct],
  );

  /** Dual live streams + measured stage — PiP stays mounted; visibility toggled separately. */
  const patrolLivePipReady = useMemo(() => {
    if (!liveMonitoring) return false;
    if (!live360StreamUrl || !livePtzStreamUrl) return false;
    if (patrolPtzPreviewPx.width <= 0 || patrolPtzPreviewPx.height <= 0) {
      return false;
    }
    return true;
  }, [
    liveMonitoring,
    live360StreamUrl,
    livePtzStreamUrl,
    patrolPtzPreviewPx.width,
    patrolPtzPreviewPx.height,
  ]);

  const sortedFloorPins = useMemo(
    () => normalizePatrolFloorPins(selectedPatrol),
    [selectedPatrol],
  );

  const currentFloorPinIndex = useMemo(() => {
    if (!sortedFloorPins.length) return 0;
    const pinId = selectedPatrol[currentVideoIndex]?.pin_id;
    const idx = sortedFloorPins.findIndex((p) => p.id === pinId);
    return idx === -1 ? 0 : idx;
  }, [sortedFloorPins, selectedPatrol, currentVideoIndex]);

  const linkHotspotBearingDeg = useMemo(() => {
    if (sortedFloorPins.length < 2) return null;
    const nextIdx = nextPatrolPinIndex(
      currentFloorPinIndex,
      sortedFloorPins.length,
    );
    const floorBearing = bearingDegForPatrolSegment(
      sortedFloorPins,
      currentFloorPinIndex,
      nextIdx,
    );
    return patrolLinkHotspotYawDeg(floorBearing);
  }, [sortedFloorPins, currentFloorPinIndex]);

  const linkHotspotTarget = useMemo(() => {
    if (sortedFloorPins.length < 2) return null;
    const floorIdx = nextPatrolPinIndex(
      currentFloorPinIndex,
      sortedFloorPins.length,
    );
    const pin = sortedFloorPins[floorIdx];
    if (!pin) return null;
    return {
      floorPinIndex: floorIdx,
      videoIndex: patrolVideoIndexForFloorPin(
        selectedPatrol,
        sortedFloorPins,
        floorIdx,
      ),
      label: pin.locationLabel ?? `L${floorIdx + 1}`,
    };
  }, [sortedFloorPins, currentFloorPinIndex, selectedPatrol]);

  // const [currentVideoIndex, setCurrentVideoIndex] = useState(initialVideoIndex);

  const [isPlaying, setIsPlaying] = useState(false);
  const [viewMode, setViewMode] = useState("normal"); // "normal", "quad", "flat"
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(1.0); // Volume range: 0-1
  const [isMuted, setIsMuted] = useState(true);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [selectedQuadView, setSelectedQuadView] = useState("front"); // "front", "rear", "left", "right"
  const [autoRotate, setAutoRotate] = useState(autoPlay); // Auto-rotate from 0 to 360 degrees

  /** Hide PTZ PiP during auto-rotation; show again after user clicks the patrol view. */
  const patrolLivePipShown = useMemo(
    () => patrolLivePipReady && !patrolPipPopupHidden && !autoRotate,
    [patrolLivePipReady, patrolPipPopupHidden, autoRotate],
  );

  const showPatrolPtzControlsOnPip =
    patrolLivePipShown &&
    patrolMainLens === "360" &&
    Boolean(livePtzCameraId) &&
    Boolean(livePtzStreamUrl);

  const togglePatrolStreamTransport = useCallback(() => {
    if (patrolMainLens === "ptz") {
      setStreamPtzTransport((prev) => (prev === "hls" ? "webrtc" : "hls"));
    } else {
      setStream360Transport((prev) => (prev === "hls" ? "webrtc" : "hls"));
    }
  }, [patrolMainLens]);

  const patrolMainStreamKindLabel = patrolMainLens === "ptz" ? "ptz" : "360";

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isImageViewerMinimized, setIsImageViewerMinimized] = useState(true);
  const [currentPinId, setCurrentPinId] = useState(null);
  const [isPictureInPictureHovered, setIsPictureInPictureHovered] =
    useState(false);
  const [showCircularProgressIcon, setShowCircularProgressIcon] =
    useState(false);
  const [isCircularProgressHovered, setIsCircularProgressHovered] =
    useState(false);
  const [isPictureInMaxPictureHovered, setIsPictureInMaxPictureHovered] =
    useState(false);
  const [isPictureInPictureCircleHovered, setIsPictureInPictureCircleHovered] =
    useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [controlsContainerWidth, setControlsContainerWidth] = useState(0);
  const [isRotationCycleComplete, setIsRotationCycleComplete] = useState(false);
  /** When set, PatrolImageViewer slides along line then onSlideComplete updates index (same as rotation complete). */
  const [slideDirection, setSlideDirection] = useState(null); // 'next' | 'prev' | null
  const [showController, setShowController] = useState(!autoPlay);

  /** 3D / quad / flat — in ⋮ menu (incl. Digital Patrol live when manual chrome is hidden). */
  const showPatrolViewModeMenu =
    isPatrolLive360Main &&
    !isPatrolLivePtzFlatMain &&
    (liveMonitoring ||
      showController ||
      videos.length > 0 ||
      Boolean(patrolLiveMainStreamUrl));

  // const[changeIndexType,setChangeIndexType] = useState(null);
  const counter = useRef(0);
  const changeIndexType = useRef(null);
  /** After first successful load, clip changes use Marzipano-style eased blend (dip → swap → restore). */
  const patrolCrossfadePrimedRef = useRef(false);
  /**
   * When floor slide **start** runs `loadVideo(toIndex)`, skip one `useEffect` run so slide-end
   * `setCurrentVideoIndex` does not load the same clip again.
   */
  const skipNextPatrolLoadVideoEffectRef = useRef(false);
  /** Floor slide target video index (pin index ≠ video index). */
  const pendingSlideTargetVideoIndexRef = useRef(null);
  const autoRotateRef = useRef(autoPlay);
  const [patrolSceneVisible, setPatrolSceneVisible] = useState(true);
  /** Equirect norm for click-to-aim focus ring (wankhede-360). */
  const [patrolFocusRingNorm, setPatrolFocusRingNorm] = useState(null);
  /** Layer ref for sample-tour opacity blends (see patrolSceneCrossfade.js). */
  const patrolSceneLayerRef = useRef(null);
  /** Stable signature of patrol URLs — avoids resetting index when `videos` is a new array with the same URLs. */
  const patrolVideosSignatureRef = useRef(undefined);

  /** Secondary `<video>` for WebGL dissolve (Marzipano-style overlap); primary stays on sphere A. */
  const patrolCrossfadeVideoRef = useRef(null);
  const crossfadeSphereRef = useRef(null);
  const crossfadeVideoTextureRef = useRef(null);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const controlsContainerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const sphereRef = useRef(null);
  const videoTextureRef = useRef(null);
  const animationFrameRef = useRef(null);
  const startMain360LoopRef = useRef(null);
  const resizeHandlerRef = useRef(null);
  /** Element resize (splitter drag, panel toggle) — not covered by window "resize". */
  const mainResizeObserverRef = useRef(null);
  /** Coalesce ResizeObserver → one setSize per frame; cancel on teardown. */
  const mainResizeRafRef = useRef(0);
  /** Last CSS layout size for main WebGL — skip redundant setSize. */
  const lastWebGLLayoutSizeRef = useRef({ w: -1, h: -1 });
  const quadLastWebGLLayoutSizeRef = useRef({});
  const quadResizeRafRefs = useRef({});
  const quadResizeObserversRef = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });

  // Quad view refs
  const quadCanvasRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadRendererRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadSceneRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadCameraRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadSphereRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });
  const quadAnimationFrameRefs = useRef({
    front: null,
    rear: null,
    left: null,
    right: null,
    main: null,
  });

  // Rotation state for different views
  const [rotation, setRotation] = useState({ yaw: 0, pitch: 0, fov: 75 });

  const [viewerAspect, setViewerAspect] = useState(16 / 9);
  const [showPatrolEquirectAssetHotspots, setShowPatrolEquirectAssetHotspots] =
    useState(readPatrolAssetHotspotsPreference);

  const patrolAssetHotspots = useMemo(
    () =>
      getPatrolAssetHotspotsForRecording(selectedPatrol[currentVideoIndex], {
        useDemoFallback: false,
      }),
    [selectedPatrol, currentVideoIndex],
  );

  /**
   * Default layout: full-width 360° with floor plan in bottom-left PiP (not docked 40/60).
   * Re-apply when the drawing URL changes so a new patrol opens in this view.
   */
  useEffect(() => {
    if (!imageUrl) return;
    setIsImageViewerMinimized(true);
    setShowCircularProgressIcon(false);
    setIsCircularProgressHovered(false);
    setIsPictureInPictureCircleHovered(false);
    setIsPictureInMaxPictureHovered(false);
    setIsPictureInPictureHovered(false);
  }, [imageUrl]);

  useEffect(() => {
    if (!liveMonitoring || patrolLiveMainStreamUrl) return;
    const rec = selectedPatrol?.[currentVideoIndex];
    if (!rec?.camera_id && !rec?.pin_id) return;
    setError("Live stream unavailable.");
  }, [liveMonitoring, patrolLiveMainStreamUrl, selectedPatrol, currentVideoIndex]);

  /** PTZ main (or PTZ-only camera) uses flat video; 360° main uses sphere WebGL. */
  useEffect(() => {
    if (!liveMonitoring) return;
    if (isLivePtzOnlyCamera || (patrolCanLensSwap && patrolMainLens === "ptz")) {
      setViewMode("flat");
      return;
    }
    if (patrolCanLensSwap && patrolMainLens === "360") {
      setViewMode("normal");
    }
  }, [
    liveMonitoring,
    patrolLiveStreamKind,
    currentVideoIndex,
    isLivePtzOnlyCamera,
    patrolCanLensSwap,
    patrolMainLens,
  ]);

  /** Turn on asset hotspots when the active recording includes fixture/API data. */
  useEffect(() => {
    const recording = selectedPatrol?.[currentVideoIndex];
    if (!hasPatrolAssetHotspots(recording)) return;
    try {
      if (
        localStorage.getItem(PATROL_EQUIRECT_ASSET_HOTSPOTS_STORAGE_KEY) != null
      ) {
        return;
      }
    } catch {
      /* ignore */
    }
    setShowPatrolEquirectAssetHotspots(true);
  }, [selectedPatrol, currentVideoIndex]);

  const isPatrol360SphereView = viewMode === "normal";

  const showPatrolAssetHotspotsLayer = useMemo(
    () =>
      showPatrolEquirectAssetHotspots &&
      isPatrol360SphereView &&
      isPatrolLive360Main &&
      patrolAssetHotspots.length > 0 &&
      !error,
    [
      showPatrolEquirectAssetHotspots,
      isPatrol360SphereView,
      isPatrolLive360Main,
      patrolAssetHotspots.length,
      error,
    ],
  );

  /** Immersive 360° main + dual PTZ — click panorama to aim PTZ (wankhede-360). */
  const patrolPanoramaPickingEnabled = useMemo(
    () =>
      patrolLivePtzAimEnabled &&
      isPatrolLive360Main &&
      viewMode === "normal" &&
      Boolean(patrolLiveMainStreamUrl) &&
      patrolSceneVisible &&
      !error &&
      patrolLivePtzAim.ptzOperable,
    [
      patrolLivePtzAimEnabled,
      isPatrolLive360Main,
      viewMode,
      patrolLiveMainStreamUrl,
      patrolSceneVisible,
      error,
      patrolLivePtzAim.ptzOperable,
    ],
  );


  const showPatrolLinkHotspot = useMemo(
    () =>
      sortedFloorPins.length > 1 &&
      linkHotspotBearingDeg != null &&
      linkHotspotTarget != null &&
      viewMode === "normal" &&
      !error,
    [
      sortedFloorPins.length,
      linkHotspotBearingDeg,
      linkHotspotTarget,
      viewMode,
      error,
    ],
  );
  const [quadRotations, setQuadRotations] = useState({
    front: { yaw: 0, pitch: 0 },
    rear: { yaw: 180, pitch: 0 },
    left: { yaw: -90, pitch: 0 },
    right: { yaw: 90, pitch: 0 },
    main: { yaw: 0, pitch: 0 },
  });
  const quadRotationsRef = useRef({
    front: { yaw: 0, pitch: 0 },
    rear: { yaw: 180, pitch: 0 },
    left: { yaw: -90, pitch: 0 },
    right: { yaw: 90, pitch: 0 },
    main: { yaw: 0, pitch: 0 },
  });
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef(null);
  /** Pointer-down origin until drag exceeds {@link PATROL_CLICK_MOVE_PX} or mouseup. */
  const patrolPointerDownRef = useRef(null);
  const isDraggingQuadRef = useRef(false);
  const lastMousePosQuadRef = useRef(null);
  const isManualControlRef = useRef(false); // Track if user is manually controlling rotation
  const currentSmoothYawRef = useRef(0); // Current smooth yaw value for interpolation
  const targetYawRef = useRef(0); // Target yaw value to interpolate towards
  const currentSmoothFovRef = useRef(75); // Current smooth FOV value for interpolation
  const targetFovRef = useRef(75); // Target FOV value to interpolate towards
  const rotationRef = useRef({ yaw: 0, pitch: 0, fov: 75 }); // Ref to track rotation without triggering re-renders
  const isResettingRef = useRef(false); // Track if we're resetting due to videos change
  const previousVideosLengthRef = useRef(videos?.length || 0); // Track previous videos length
  const rotationTimerRef = useRef(0); // Timer for fixed rotation speed (0-6 seconds)
  const rotationStartTimeRef = useRef(null); // Start time for current rotation cycle
  const lastYawBeforePauseRef = useRef(0); // Store yaw angle when paused
  const videosRef = useRef(videos); // Ref to always have latest videos array
  const currentVideoIndexRef = useRef(currentVideoIndex); // Ref to always have latest video index
  const lastStateUpdateTimeRef = useRef(0); // Track last time we updated rotation state
  const lastFovStateUpdateTimeRef = useRef(0);
  const rotationDurationMs = useMemo(
    () => patrolRotationDurationMs(rotationDurationSeconds),
    [rotationDurationSeconds],
  );

  useEffect(() => {
    autoRotateRef.current = autoRotate;
  }, [autoRotate]);

  /** Keep auto-rotate flag, manual chrome (prev/next), and patrol button icon in sync. */
  const pausePatrolAutoRotation = useCallback(() => {
    autoRotateRef.current = false;
    setAutoRotate(false);
    setShowController(true);
    isManualControlRef.current = true;
    rotationStartTimeRef.current = null;
    lastYawBeforePauseRef.current = rotationRef.current.yaw;
  }, []);

  const enablePatrolAutoRotationUi = useCallback(() => {
    autoRotateRef.current = true;
    setAutoRotate(true);
    setShowController(false);
    isManualControlRef.current = false;
    if (liveMonitoring && patrolCanLensSwap) {
      setPatrolMainLens("360");
    }
  }, [liveMonitoring, patrolCanLensSwap]);

  /** Swap 360° ↔ PTZ main/PiP (wankhede-360 tap PiP or toolbar). */
  const swapPatrolMainLens = useCallback(() => {
    if (!patrolCanLensSwap) return;
    if (autoRotateRef.current) {
      pausePatrolAutoRotation();
    }
    setPatrolMainLens((prev) => (prev === "360" ? "ptz" : "360"));
  }, [patrolCanLensSwap, pausePatrolAutoRotation]);

  const togglePatrolMainLens = swapPatrolMainLens;

  const patrolPipTapRef = useRef(null);

  const onPatrolPipPointerDown = useCallback(
    (e) => {
      if (!patrolCanLensSwap || !patrolLivePipShown) return;
      if (isPatrolPipControlTarget(e.target)) return;
      patrolPipTapRef.current = { x: e.clientX, y: e.clientY };
    },
    [patrolCanLensSwap, patrolLivePipShown, isPatrolPipControlTarget],
  );

  const onPatrolPipPointerUp = useCallback(
    (e) => {
      if (!patrolCanLensSwap || !patrolLivePipShown) return;
      const tap = patrolPipTapRef.current;
      patrolPipTapRef.current = null;
      if (!tap || isPatrolPipControlTarget(e.target)) return;
      const dx = e.clientX - tap.x;
      const dy = e.clientY - tap.y;
      if (dx * dx + dy * dy < 400) {
        swapPatrolMainLens();
      }
    },
    [
      patrolCanLensSwap,
      patrolLivePipShown,
      isPatrolPipControlTarget,
      swapPatrolMainLens,
    ],
  );

  const triggerPatrolPipSwap = useCallback(() => {
    if (!patrolCanLensSwap || !patrolLivePipShown) return;
    patrolPipTapRef.current = null;
    swapPatrolMainLens();
  }, [patrolCanLensSwap, patrolLivePipShown, swapPatrolMainLens]);

  /** Tap PTZ PiP (video/chrome) → promote PTZ to main view. */
  const handlePatrolPipSwapClick = useCallback(
    (e) => {
      if (isPatrolPipControlTarget(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      triggerPatrolPipSwap();
    },
    [isPatrolPipControlTarget, triggerPatrolPipSwap],
  );

  /** Re-anchor auto-rotation clock when speed changes so 25–50s settings take effect immediately. */
  useEffect(() => {
    if (!autoRotate || isManualControlRef.current) return;
    if (rotationStartTimeRef.current === null) return;
    const now = performance.now();
    const currentYaw = ((rotationRef.current.yaw % 360) + 360) % 360;
    rotationStartTimeRef.current =
      now - (currentYaw / 360) * rotationDurationMs;
  }, [rotationDurationMs, autoRotate]);

  const STATE_UPDATE_INTERVAL = 50; // Update state every 50ms during auto-rotation (20fps for UI updates)
  const lastCycleIndexRef = useRef(0);

  /** Remove dissolve sphere + texture before rebuilding or disposing WebGL. */
  const disposePatrolGlCrossfade = useCallback(() => {
    const scen = sceneRef.current;
    const cf = crossfadeSphereRef.current;
    if (cf && scen && cf.parent === scen) {
      scen.remove(cf);
    }
    if (crossfadeVideoTextureRef.current) {
      crossfadeVideoTextureRef.current.dispose();
      crossfadeVideoTextureRef.current = null;
    }
    crossfadeSphereRef.current = null;
  }, []);

  /** Stop main 360 RAF and resize listeners without disposing WebGL (quad view switch). */
  const detachMain360WebGLResources = useCallback(() => {
    if (mainResizeRafRef.current) {
      cancelAnimationFrame(mainResizeRafRef.current);
      mainResizeRafRef.current = 0;
    }
    if (mainResizeObserverRef.current) {
      mainResizeObserverRef.current.disconnect();
      mainResizeObserverRef.current = null;
    }
    if (resizeHandlerRef.current) {
      window.removeEventListener("resize", resizeHandlerRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  /** Tear down main 360 WebGL (flat mode or full remount). */
  const disposeMain360Renderer = useCallback(() => {
    detachMain360WebGLResources();
    resizeHandlerRef.current = null;
    disposePatrolGlCrossfade();
    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = null;
    }
    if (videoTextureRef.current) {
      videoTextureRef.current.dispose();
      videoTextureRef.current = null;
    }
    sceneRef.current = null;
    cameraRef.current = null;
    sphereRef.current = null;
    startMain360LoopRef.current = null;
  }, [detachMain360WebGLResources, disposePatrolGlCrossfade]);

  /**
   * Initialize Three.js scene for 360-degree video rendering
   */
  const init360Renderer = useCallback(
    (canvas, video, initialFov = 75) => {
      if (!canvas || !video) return null;

      // Create renderer with improved settings for better quality
      const renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
        precision: "highp",
      });

      // Allow higher pixel ratio for better quality on high-DPI displays
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3));

      // Get dimensions from parent container to ensure 100% sizing
      const parent = canvas.parentElement;
      const rawW = parent
        ? parent.clientWidth
        : canvas.clientWidth || canvas.offsetWidth;
      const rawH = parent
        ? parent.clientHeight
        : canvas.clientHeight || canvas.offsetHeight;
      const width = Math.max(1, rawW);
      const height = Math.max(1, rawH);

      renderer.setSize(width, height);
      renderer.setClearColor(0x000000, 1.0);

      if (renderer.outputColorSpace !== undefined) {
        renderer.outputColorSpace = THREE.SRGBColorSpace;
      }

      // Create scene
      const scene = new THREE.Scene();

      // Create camera (using dimensions already calculated above)
      const camera = new THREE.PerspectiveCamera(
        initialFov,
        width / height,
        0.01,
        200,
      );
      camera.position.set(0, 0, 0);

      const videoTexture = createPatrolEquirectVideoTexture(video);
      const sphere = createPatrolEquirectSphereMesh(videoTexture);
      scene.add(sphere);

      return { renderer, scene, camera, sphere, videoTexture };
    },
    [videos],
  );

  /**
   * Initialize main 360 renderer
   * @param {boolean} [reattachOnly] — resume RAF/resize after quad view (keep WebGL context)
   */
  const initMain360Renderer = useCallback((reattachOnly = false) => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const layerEl = patrolSceneLayerRef.current;
    if (layerEl) {
      layerEl.style.opacity = "1";
    }

    if (
      reattachOnly &&
      rendererRef.current &&
      sceneRef.current &&
      cameraRef.current &&
      resizeHandlerRef.current
    ) {
      detachMain360WebGLResources();
      const handleResize = resizeHandlerRef.current;
      handleResize();
      const resizeTarget = canvas.parentElement ?? canvas;
      if (typeof ResizeObserver !== "undefined" && resizeTarget) {
        const scheduleMainResize = () => {
          if (mainResizeRafRef.current) return;
          mainResizeRafRef.current = requestAnimationFrame(() => {
            mainResizeRafRef.current = 0;
            resizeHandlerRef.current?.();
          });
        };
        const ro = new ResizeObserver(() => {
          scheduleMainResize();
        });
        ro.observe(resizeTarget);
        mainResizeObserverRef.current = ro;
      }
      window.addEventListener("resize", handleResize);
      startMain360LoopRef.current?.();
      return;
    }

    disposeMain360Renderer();

    const result = init360Renderer(canvas, video, currentSmoothFovRef.current);
    if (!result) return;

    rendererRef.current = result.renderer;
    sceneRef.current = result.scene;
    cameraRef.current = result.camera;
    sphereRef.current = result.sphere;
    videoTextureRef.current = result.videoTexture;

    const cfVid = patrolCrossfadeVideoRef.current;
    if (cfVid && result.scene && result.sphere) {
      const cfTex = createPatrolEquirectVideoTexture(cfVid);
      const cfSphere = createPatrolEquirectSphereMesh(cfTex, {
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      cfSphere.visible = false;
      cfSphere.renderOrder = 1;
      result.sphere.renderOrder = 0;
      result.scene.add(cfSphere);

      crossfadeSphereRef.current = cfSphere;
      crossfadeVideoTextureRef.current = cfTex;
    }

    {
      const parent0 = canvas.parentElement;
      const rawW0 = parent0
        ? parent0.clientWidth
        : canvas.clientWidth || canvas.offsetWidth;
      const rawH0 = parent0
        ? parent0.clientHeight
        : canvas.clientHeight || canvas.offsetHeight;
      const w0 = Math.max(1, rawW0);
      const h0 = Math.max(1, rawH0);
      lastWebGLLayoutSizeRef.current = { w: w0, h: h0 };
    }

    // Setup resize handler
    const handleResize = () => {
      if (
        !canvas ||
        !rendererRef.current ||
        !cameraRef.current ||
        !sceneRef.current
      )
        return;
      // Get dimensions from parent container to ensure 100% sizing
      const parent = canvas.parentElement;
      const rawW = parent
        ? parent.clientWidth
        : canvas.clientWidth || canvas.offsetWidth;
      const rawH = parent
        ? parent.clientHeight
        : canvas.clientHeight || canvas.offsetHeight;
      const width = Math.max(1, rawW);
      const height = Math.max(1, rawH);

      const last = lastWebGLLayoutSizeRef.current;
      if (last.w === width && last.h === height) return;
      lastWebGLLayoutSizeRef.current = { w: width, h: height };

      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      const scene = sceneRef.current;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);

      const videoTexture = videoTextureRef.current;
      if (videoTexture && video && video.readyState >= 2) {
        videoTexture.needsUpdate = true;
      }
      renderer.render(scene, camera);
    };

    resizeHandlerRef.current = handleResize;
    if (mainResizeObserverRef.current) {
      mainResizeObserverRef.current.disconnect();
      mainResizeObserverRef.current = null;
    }
    handleResize();
    const resizeTarget = canvas.parentElement ?? canvas;
    if (typeof ResizeObserver !== "undefined" && resizeTarget) {
      const scheduleMainResize = () => {
        if (mainResizeRafRef.current) return;
        mainResizeRafRef.current = requestAnimationFrame(() => {
          mainResizeRafRef.current = 0;
          resizeHandlerRef.current?.();
        });
      };
      const ro = new ResizeObserver(() => {
        scheduleMainResize();
      });
      ro.observe(resizeTarget);
      mainResizeObserverRef.current = ro;
    }
    window.addEventListener("resize", handleResize);

    // Render loop
    const animate = () => {
      if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const videoTexture = videoTextureRef.current;
      const video = videoRef.current;

      const now = performance.now();

      // =====================================================
      // AUTO ROTATION (DETERMINISTIC + CYCLE SAFE)
      // =====================================================
      if (autoRotateRef.current && video && !isManualControlRef.current) {
        if (rotationStartTimeRef.current === null) {
          rotationStartTimeRef.current = now;
          lastStateUpdateTimeRef.current = now;
        }

        const elapsed = now - rotationStartTimeRef.current;
        const progress = Math.min(elapsed / rotationDurationMs, 1);

        const yaw = progress * 360;

        rotationRef.current.yaw = yaw;
        currentSmoothYawRef.current = yaw;

        if (now - lastStateUpdateTimeRef.current >= STATE_UPDATE_INTERVAL) {
          setRotation((prev) => ({
            ...prev,
            yaw,
          }));
          lastStateUpdateTimeRef.current = now;
        }

        if (progress >= 1) {
          changeIndexType.current = "auto";
          autoRotateRef.current = false;
          setAutoRotate(false);
          setShowController(true);
          isManualControlRef.current = true;
          rotationStartTimeRef.current = null;
          setIsRotationCycleComplete(true);
        }
      } else {
        // Reset start time when auto-rotate stops
        if (rotationStartTimeRef.current !== null) {
          rotationStartTimeRef.current = null;
        }
      }

      let fovApplied = currentSmoothFovRef.current;
      if (camera) {
        currentSmoothFovRef.current = THREE.MathUtils.lerp(
          currentSmoothFovRef.current,
          targetFovRef.current,
          PATROL_FOV_LERP_FACTOR,
        );
        fovApplied = currentSmoothFovRef.current;
        rotationRef.current.fov = fovApplied;

        if (now - lastFovStateUpdateTimeRef.current >= STATE_UPDATE_INTERVAL) {
          setRotation((prev) => {
            if (Math.abs(prev.fov - fovApplied) < 0.02) {
              return prev;
            }
            return { ...prev, fov: fovApplied };
          });
          lastFovStateUpdateTimeRef.current = now;
        }
      }

      // =====================================================
      // APPLY CAMERA TRANSFORMS
      // =====================================================
      camera.rotation.order = "YXZ";
      camera.rotation.y = THREE.MathUtils.degToRad(rotationRef.current.yaw);
      camera.rotation.x = THREE.MathUtils.degToRad(rotationRef.current.pitch);

      camera.fov = fovApplied;
      camera.updateProjectionMatrix();

      // =====================================================
      // UPDATE VIDEO TEXTURE
      // =====================================================
      if (videoTexture && video && video.readyState >= 2) {
        videoTexture.needsUpdate = true;
      }

      const cfTex = crossfadeVideoTextureRef.current;
      const cfVid = patrolCrossfadeVideoRef.current;
      const cfSphere = crossfadeSphereRef.current;
      if (
        cfTex &&
        cfVid &&
        cfSphere?.visible &&
        cfVid.readyState >= 2
      ) {
        cfTex.needsUpdate = true;
      }

      renderer.render(scene, camera);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    startMain360LoopRef.current = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animate();
    };
    startMain360LoopRef.current();
  }, [
    detachMain360WebGLResources,
    disposeMain360Renderer,
    disposePatrolGlCrossfade,
    init360Renderer,
    rotationDurationMs,
  ]);

  /**
   * Initialize quad view renderers
   */
  const initQuadViewRenderers = useCallback(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const views = ["front", "rear", "left", "right", "main"];

    views.forEach((view) => {
      const canvas = quadCanvasRefs.current[view];
      if (!canvas) return;

      // Cleanup existing renderer
      if (quadAnimationFrameRefs.current[view]) {
        cancelAnimationFrame(quadAnimationFrameRefs.current[view]);
      }
      if (quadRendererRefs.current[view]) {
        quadRendererRefs.current[view].dispose();
      }

      const result = init360Renderer(canvas, video, 75);
      if (!result) return;

      quadRendererRefs.current[view] = result.renderer;
      quadSceneRefs.current[view] = result.scene;
      quadCameraRefs.current[view] = result.camera;
      quadSphereRefs.current[view] = result.sphere;

      {
        const parentQ = canvas.parentElement;
        const rawWQ = parentQ
          ? parentQ.clientWidth
          : canvas.clientWidth || canvas.offsetWidth;
        const rawHQ = parentQ
          ? parentQ.clientHeight
          : canvas.clientHeight || canvas.offsetHeight;
        quadLastWebGLLayoutSizeRef.current[view] = {
          w: Math.max(1, rawWQ),
          h: Math.max(1, rawHQ),
        };
      }

      // Setup resize handler for each view
      const handleResize = () => {
        if (
          !canvas ||
          !quadRendererRefs.current[view] ||
          !quadCameraRefs.current[view] ||
          !quadSceneRefs.current[view]
        )
          return;
        // Get dimensions from parent container to ensure 100% sizing
        const parent = canvas.parentElement;
        const rawW = parent
          ? parent.clientWidth
          : canvas.clientWidth || canvas.offsetWidth;
        const rawH = parent
          ? parent.clientHeight
          : canvas.clientHeight || canvas.offsetHeight;
        const width = Math.max(1, rawW);
        const height = Math.max(1, rawH);

        const lastQ = quadLastWebGLLayoutSizeRef.current[view];
        if (lastQ && lastQ.w === width && lastQ.h === height) return;
        quadLastWebGLLayoutSizeRef.current[view] = { w: width, h: height };

        const renderer = quadRendererRefs.current[view];
        const camera = quadCameraRefs.current[view];
        const scene = quadSceneRefs.current[view];
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);

        const videoTexture = result.videoTexture;
        if (videoTexture && video.readyState >= 2) {
          videoTexture.needsUpdate = true;
        }
        renderer.render(scene, camera);
      };

      if (quadResizeObserversRef.current[view]) {
        quadResizeObserversRef.current[view].disconnect();
        quadResizeObserversRef.current[view] = null;
      }
      if (quadResizeRafRefs.current[view]) {
        cancelAnimationFrame(quadResizeRafRefs.current[view]);
        quadResizeRafRefs.current[view] = 0;
      }
      handleResize();
      const quadResizeTarget = canvas.parentElement ?? canvas;
      if (typeof ResizeObserver !== "undefined" && quadResizeTarget) {
        const scheduleQuadResize = () => {
          if (quadResizeRafRefs.current[view]) return;
          quadResizeRafRefs.current[view] = requestAnimationFrame(() => {
            quadResizeRafRefs.current[view] = 0;
            handleResize();
          });
        };
        const ro = new ResizeObserver(() => scheduleQuadResize());
        ro.observe(quadResizeTarget);
        quadResizeObserversRef.current[view] = ro;
      }

      // Render loop for each view
      const animate = () => {
        if (
          !quadRendererRefs.current[view] ||
          !quadSceneRefs.current[view] ||
          !quadCameraRefs.current[view]
        )
          return;

        const camera = quadCameraRefs.current[view];
        const renderer = quadRendererRefs.current[view];
        const scene = quadSceneRefs.current[view];
        const videoTexture = result.videoTexture;
        const rot = quadRotationsRef.current[view] || { yaw: 0, pitch: 0 };

        // Set camera rotation based on view
        camera.rotation.order = "YXZ";
        camera.rotation.y = THREE.MathUtils.degToRad(rot.yaw);
        camera.rotation.x = THREE.MathUtils.degToRad(rot.pitch);
        camera.updateProjectionMatrix();

        // Update video texture
        if (videoTexture && video.readyState >= 2) {
          videoTexture.needsUpdate = true;
        }

        renderer.render(scene, camera);
        quadAnimationFrameRefs.current[view] = requestAnimationFrame(animate);
      };

      animate();
    });
  }, [init360Renderer]);

  /**
   * Handle mouse down for 360 rotation
   */
  const handleMouseDown = useCallback(
    (e) => {
      if (viewMode === "flat") return;
      // In quad view, only allow dragging on the main view (not on mini views)
      if (viewMode === "quad") {
        const target = e.target;
        // Check if click is on a mini view canvas (left side)
        if (target.closest && target.closest("[data-quad-mini-view]")) {
          return; // Don't allow dragging on mini views
        }
        patrolPointerDownRef.current = { x: e.clientX, y: e.clientY };
        isDraggingQuadRef.current = false;
        lastMousePosQuadRef.current = null;
        isManualControlRef.current = true;
        currentSmoothYawRef.current = rotation.yaw;
        targetYawRef.current = rotation.yaw;
      } else {
        patrolPointerDownRef.current = { x: e.clientX, y: e.clientY };
        isDraggingRef.current = false;
        lastMousePosRef.current = null;
        isManualControlRef.current = true;
        currentSmoothYawRef.current = rotation.yaw;
        targetYawRef.current = rotation.yaw;
      }
    },
    [viewMode, rotation.yaw],
  );

  /**
   * Handle mouse move for 360 rotation
   */
  const handleMouseMove = useCallback(
    (e) => {
      if (viewMode === "flat") return;

      if (viewMode === "quad") {
        if (patrolPointerDownRef.current && !isDraggingQuadRef.current) {
          const dx0 = e.clientX - patrolPointerDownRef.current.x;
          const dy0 = e.clientY - patrolPointerDownRef.current.y;
          if (
            dx0 * dx0 + dy0 * dy0 >
            PATROL_CLICK_MOVE_PX * PATROL_CLICK_MOVE_PX
          ) {
            isDraggingQuadRef.current = true;
            lastMousePosQuadRef.current = { x: e.clientX, y: e.clientY };
          }
        }
        if (!isDraggingQuadRef.current || !lastMousePosQuadRef.current) return;

        const dx = e.clientX - lastMousePosQuadRef.current.x;
        const dy = e.clientY - lastMousePosQuadRef.current.y;

        const SENSITIVITY = 0.3;

        setQuadRotations((prev) => {
          const currentRot = prev[selectedQuadView] || prev.main;
          const newYaw = currentRot.yaw + dx * SENSITIVITY;
          const newPitch = Math.max(
            -89,
            Math.min(89, currentRot.pitch + dy * SENSITIVITY),
          );
          const newRotations = {
            ...prev,
            [selectedQuadView]: { yaw: newYaw, pitch: newPitch },
            main: { yaw: newYaw, pitch: newPitch },
          };
          quadRotationsRef.current = newRotations;
          return newRotations;
        });

        lastMousePosQuadRef.current = { x: e.clientX, y: e.clientY };
      } else {
        if (patrolPointerDownRef.current && !isDraggingRef.current) {
          const dx0 = e.clientX - patrolPointerDownRef.current.x;
          const dy0 = e.clientY - patrolPointerDownRef.current.y;
          if (
            dx0 * dx0 + dy0 * dy0 >
            PATROL_CLICK_MOVE_PX * PATROL_CLICK_MOVE_PX
          ) {
            isDraggingRef.current = true;
            lastMousePosRef.current = { x: e.clientX, y: e.clientY };
          }
        }
        if (!isDraggingRef.current || !lastMousePosRef.current) return;

        const dx = e.clientX - lastMousePosRef.current.x;
        const dy = e.clientY - lastMousePosRef.current.y;

        const SENSITIVITY = 0.3;

        setRotation((prev) => {
          const newYaw = prev.yaw + dx * SENSITIVITY;
          const newPitch = Math.max(
            -89,
            Math.min(89, prev.pitch + dy * SENSITIVITY),
          );
          // Update rotation ref to keep it in sync
          rotationRef.current.yaw = newYaw;
          rotationRef.current.pitch = newPitch;
          return {
            ...prev,
            yaw: newYaw,
            pitch: newPitch,
          };
        });

        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      }
    },
    [viewMode, selectedQuadView],
  );

  /** Click immersive 360° main → aim PTZ to that panorama point (wankhede-360). */
  const tryPatrolPanoramaPick = useCallback(
    (e) => {
      if (!patrolPanoramaPickingEnabled) return;
      const target = e?.target;
      if (
        target?.closest?.(
          "button, [role='button'], .patrol-preset-grid-layer button, .patrol-live-ptz-pip, .ds-preview-pip, .ds-preview-pip__edge-controls, .ds-ptz-btn, .MuiIconButton-root",
        )
      ) {
        return;
      }

      const canvas = canvasRef.current;
      const camera = cameraRef.current;
      const sphere = sphereRef.current;
      if (!canvas || !camera || !sphere) return;

      camera.rotation.order = "YXZ";
      camera.rotation.y = THREE.MathUtils.degToRad(rotationRef.current.yaw);
      camera.rotation.x = THREE.MathUtils.degToRad(rotationRef.current.pitch);
      camera.fov = rotationRef.current.fov;
      camera.updateProjectionMatrix();

      if (patrolLivePtzAim.usesPresetGridPanoramaPick) {
        const host = patrolSceneLayerRef.current;
        const gridId = pickNearestMappedPresetGridId({
          clientX: e.clientX,
          clientY: e.clientY,
          canvas,
          camera,
          hostEl: host,
          mappedGridIds: patrolLivePtzAim.mappedPresetGridIds,
        });
        if (gridId != null) {
          patrolLivePtzAim.handlePresetGridSelect(gridId);
          revealPatrolLivePtzPip();
        }
        return;
      }

      const norms = samplePatrolEquirectNormFromClient(
        canvas,
        camera,
        sphere,
        e.clientX,
        e.clientY,
      );
      if (norms) {
        setPatrolFocusRingNorm({ normX: norms.normX, normY: norms.normY });
        patrolLivePtzAim.applyPanoramaAimFromNorm(norms.normX, norms.normY);
        revealPatrolLivePtzPip();
      }
    },
    [
      patrolPanoramaPickingEnabled,
      patrolLivePtzAim,
      patrolLivePtzAim.usesPresetGridPanoramaPick,
      revealPatrolLivePtzPip,
    ],
  );

  /**
   * Handle mouse up for 360 rotation
   */
  const handleMouseUp = useCallback(
    (e) => {
      const wasMainDrag = isDraggingRef.current;
      const wasQuadDrag = isDraggingQuadRef.current;
      const hadPointer = patrolPointerDownRef.current != null;

      isDraggingRef.current = false;
      lastMousePosRef.current = null;
      isDraggingQuadRef.current = false;
      lastMousePosQuadRef.current = null;
      patrolPointerDownRef.current = null;

      if (
        e &&
        hadPointer &&
        !wasMainDrag &&
        !wasQuadDrag &&
        viewMode === "normal"
      ) {
        if (liveMonitoring && patrolCanLensSwap) {
          revealPatrolLivePtzPip();
        }
        tryPatrolPanoramaPick(e);
      }

      // Resume auto-rotation after a short delay, preserving current yaw
      setTimeout(() => {
        if (!autoRotateRef.current) return;
        isManualControlRef.current = false;
        const rawYaw = rotationRef.current.yaw;
        const resumeYaw =
          rawYaw >= 360 ? 359.99 : Math.min(Math.max(rawYaw % 360, 0), 359.99);
        currentSmoothYawRef.current = resumeYaw;
        targetYawRef.current = resumeYaw;
        const elapsedOffset = (resumeYaw / 360) * rotationDurationMs;
        rotationStartTimeRef.current = performance.now() - elapsedOffset;
        rotationTimerRef.current = elapsedOffset;
        lastStateUpdateTimeRef.current = performance.now();
      }, 100);
    },
    [
      liveMonitoring,
      patrolCanLensSwap,
      revealPatrolLivePtzPip,
      rotationDurationMs,
      tryPatrolPanoramaPick,
      viewMode,
    ],
  );

  /**
   * Handle wheel for zoom in 360 mode
   */
  const handleWheel = useCallback(
    (e) => {
      if (viewMode === "flat" || viewMode === "quad") return;
      e.preventDefault();

      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        pausePatrolAutoRotation();
        const newYaw =
          rotationRef.current.yaw + e.deltaX * PATROL_WHEEL_PAN_STEP;
        rotationRef.current.yaw = newYaw;
        currentSmoothYawRef.current = newYaw;
        targetYawRef.current = newYaw;
        setRotation((prev) => ({ ...prev, yaw: newYaw }));
        return;
      }

      const zoomDelta = e.deltaY > 0 ? PATROL_WHEEL_ZOOM_STEP : -PATROL_WHEEL_ZOOM_STEP;
      const newFov = Math.max(
        PATROL_FOV_MIN,
        Math.min(PATROL_FOV_MAX, targetFovRef.current + zoomDelta),
      );
      targetFovRef.current = newFov;
      currentSmoothFovRef.current = newFov;
      rotationRef.current.fov = newFov;
      setRotation((prev) => ({ ...prev, fov: newFov }));
    },
    [viewMode, pausePatrolAutoRotation],
  );

  /** Begin a fresh 0→360° patrol spin on the current clip (after floor slide reaches the pin). */
  const startPatrolAutoRotation = useCallback(() => {
    currentSmoothYawRef.current = 0;
    targetYawRef.current = 0;
    rotationTimerRef.current = 0;
    rotationStartTimeRef.current = performance.now();
    lastStateUpdateTimeRef.current = performance.now();
    const carryFov = currentSmoothFovRef.current;
    rotationRef.current = { yaw: 0, pitch: 0, fov: carryFov };
    setRotation((prev) => ({ ...prev, yaw: 0, pitch: 0 }));
    isManualControlRef.current = false;
    setIsRotationCycleComplete(false);
    enablePatrolAutoRotationUi();
  }, [enablePatrolAutoRotationUi]);

  /**
   * @param {number} index
   * @param {{ deferAutoRotate?: boolean, resetYaw?: boolean }} [options]
   *   `deferAutoRotate` — floor slide start: crossfade only, spin resumes when slide completes.
   *   `resetYaw` — snap view to 0° (default true).
   */
  const loadVideo = useCallback(
    async (index, options = {}) => {
      const { deferAutoRotate = false, resetYaw = true } = options;
      if (
        !videos ||
        videos.length === 0 ||
        index < 0 ||
        index >= videos.length
      ) {
        setError("No videos available");
        return false;
      }

      setError(null);

      const video = videoRef.current;
      if (!video) {
        setPatrolSceneVisible(true);
        return false;
      }

      try {
        const layerEl = patrolSceneLayerRef.current;
        setPatrolSceneVisible(true);

        const useGlDissolve =
          patrolCrossfadePrimedRef.current &&
          viewMode === "normal" &&
          sphereRef.current &&
          crossfadeSphereRef.current &&
          patrolCrossfadeVideoRef.current;

        if (useGlDissolve) {
          if (layerEl) {
            layerEl.style.opacity = "1";
          }

          const vidB = patrolCrossfadeVideoRef.current;
          const sphere = sphereRef.current;
          const cfMesh = crossfadeSphereRef.current;
          const matA = sphere.material;
          const matB = cfMesh.material;

          const targetUrl = videos[index];

          vidB.src = targetUrl;
          vidB.load();

          await new Promise((resolve, reject) => {
            const onData = () => {
              vidB.removeEventListener("loadeddata", onData);
              vidB.removeEventListener("error", onErr);
              resolve();
            };
            const onErr = (e) => {
              vidB.removeEventListener("loadeddata", onData);
              vidB.removeEventListener("error", onErr);
              reject(e);
            };
            vidB.addEventListener("loadeddata", onData);
            vidB.addEventListener("error", onErr);
          });

          vidB.muted = video.muted;
          vidB.volume = video.volume;
          try {
            await vidB.play();
          } catch {
            /* ignore */
          }

          matA.transparent = true;
          matA.depthWrite = false;
          matB.transparent = true;
          matB.depthWrite = false;
          cfMesh.visible = true;
          matB.opacity = 0;
          matA.opacity = 1;

          await animatePatrolMarzipanoSphereCrossfade(
            matA,
            matB,
            PATROL_SAMPLE_TOUR_TRANSITION_MS,
          );

          const carryTime = vidB.currentTime;

          video.pause();
          video.src = targetUrl;
          video.load();

          await new Promise((resolve, reject) => {
            const handleLoadedData = () => {
              video.removeEventListener("loadeddata", handleLoadedData);
              video.removeEventListener("error", handleError);
              resolve();
            };
            const handleError = (e) => {
              video.removeEventListener("loadeddata", handleLoadedData);
              video.removeEventListener("error", handleError);
              reject(e);
            };
            video.addEventListener("loadeddata", handleLoadedData);
            video.addEventListener("error", handleError);
          });

          video.currentTime = carryTime;
          await video.play().catch(() => {});

          matB.opacity = 0;
          cfMesh.visible = false;
          vidB.pause();
          vidB.removeAttribute("src");
          vidB.load();

          matA.opacity = 1;
          matA.transparent = false;
          matA.depthWrite = true;

          if (videoTextureRef.current) {
            videoTextureRef.current.needsUpdate = true;
          }

          if (resetYaw) {
            currentSmoothYawRef.current = 0;
            targetYawRef.current = 0;
            rotationTimerRef.current = 0;
            rotationStartTimeRef.current = null;
            const carryFov = currentSmoothFovRef.current;
            rotationRef.current = { yaw: 0, pitch: 0, fov: carryFov };
            setRotation((prev) => ({ ...prev, yaw: 0, pitch: 0 }));
          }
          isManualControlRef.current = deferAutoRotate;
          setIsPlaying(true);

          patrolCrossfadePrimedRef.current = true;

          if (index !== 0) {
            counter.current++;
          }
          if (!deferAutoRotate) {
            if (counter.current === 0) {
              if (autoPlay) {
                enablePatrolAutoRotationUi();
              } else {
                pausePatrolAutoRotation();
              }
            } else if (changeIndexType.current === "auto") {
              enablePatrolAutoRotationUi();
            }
          }

          if (viewMode === "quad") {
            requestAnimationFrame(() => {
              initQuadViewRenderers();
            });
          }
          return true;
        }

        if (patrolCrossfadePrimedRef.current && layerEl) {
          if (viewMode === "flat") {
            await patrolSampleTourBlendDown(layerEl);
          } else {
            layerEl.style.opacity = "1";
          }
        } else if (layerEl) {
          layerEl.style.opacity = "1";
        }

        video.src = videos[index];
        video.load();

        await new Promise((resolve, reject) => {
          const handleLoadedData = () => {
            video.removeEventListener("loadeddata", handleLoadedData);
            video.removeEventListener("error", handleError);
            resolve();
          };

          const handleError = (e) => {
            video.removeEventListener("loadeddata", handleLoadedData);
            video.removeEventListener("error", handleError);
            reject(e);
          };

          video.addEventListener("loadeddata", handleLoadedData);
          video.addEventListener("error", handleError);
        });

        if (resetYaw) {
          currentSmoothYawRef.current = 0;
          targetYawRef.current = 0;
          rotationTimerRef.current = 0;
          rotationStartTimeRef.current = null;
          const carryFov = currentSmoothFovRef.current;
          rotationRef.current = { yaw: 0, pitch: 0, fov: carryFov };
          setRotation((prev) => ({ ...prev, yaw: 0, pitch: 0 }));
        }
        isManualControlRef.current = deferAutoRotate;
        await video.play();
        setIsPlaying(true);

        if (patrolCrossfadePrimedRef.current && layerEl) {
          if (viewMode === "flat") {
            await patrolSampleTourBlendUp(layerEl);
          } else {
            layerEl.style.opacity = "1";
          }
        } else if (layerEl) {
          layerEl.style.opacity = "1";
        }

        if (viewMode === "quad") {
          requestAnimationFrame(() => {
            initQuadViewRenderers();
          });
        }

        patrolCrossfadePrimedRef.current = true;

        if (index !== 0) {
          counter.current++;
        }
        if (!deferAutoRotate) {
          if (counter.current === 0) {
            if (autoPlay) {
              enablePatrolAutoRotationUi();
            } else {
              pausePatrolAutoRotation();
            }
          } else if (changeIndexType.current === "auto") {
            enablePatrolAutoRotationUi();
          }
        }
        return true;
      } catch (err) {
        skipNextPatrolLoadVideoEffectRef.current = false;
        const sphere = sphereRef.current;
        const cfMesh = crossfadeSphereRef.current;
        const matA = sphere?.material;
        if (matA) {
          matA.opacity = 1;
          matA.transparent = false;
          matA.depthWrite = true;
        }
        if (cfMesh) {
          cfMesh.visible = false;
          const matB = cfMesh.material;
          if (matB) {
            matB.opacity = 0;
            matB.depthWrite = false;
          }
        }
        const vb = patrolCrossfadeVideoRef.current;
        if (vb) {
          try {
            vb.pause();
            vb.removeAttribute("src");
            vb.load();
          } catch {
            /* ignore */
          }
        }
        setError("Video not sync yet.Please wait...");
        patrolSceneLayerRef.current?.style.setProperty("opacity", "1");
        setPatrolSceneVisible(true);
        return false;
      }
    },
    [
      videos,
      autoPlay,
      viewMode,
      initQuadViewRenderers,
      enablePatrolAutoRotationUi,
      pausePatrolAutoRotation,
    ],
  );

  /**
   * Swap 360 clip when the patrol circle **starts** the floor segment (origin → target). Parent index updates at slide end;
   * skip ref avoids a second `loadVideo` when that happens.
   */
  const handlePatrolFloorSlideStart = useCallback(
    async ({ toIndex }) => {
      const list = videosRef.current;
      if (!Array.isArray(list) || list.length === 0) return;
      if (!Number.isInteger(toIndex) || toIndex < 0) return;

      const videoIndex = patrolVideoIndexForFloorPin(
        selectedPatrol,
        sortedFloorPins,
        toIndex,
      );
      if (videoIndex < 0 || videoIndex >= list.length) return;
      if (videoIndex === currentVideoIndexRef.current) return;
      if (!patrolCrossfadePrimedRef.current) return;

      pendingSlideTargetVideoIndexRef.current = videoIndex;
      skipNextPatrolLoadVideoEffectRef.current = true;
      const ok = await loadVideo(videoIndex, {
        deferAutoRotate: true,
        resetYaw: true,
      });
      if (!ok) {
        skipNextPatrolLoadVideoEffectRef.current = false;
        pendingSlideTargetVideoIndexRef.current = null;
      }
    },
    [loadVideo, selectedPatrol, sortedFloorPins],
  );

  /**
   * Handle play/pause toggle
   */
  const handlePlayPause = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (isPlaying) {
        video.pause();
        setIsPlaying(false);
      } else {
        await video.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error("Error playing/pausing video:", err);
    }
  }, [isPlaying]);

  /** Tap main PTZ flat video → swap back to 360° on main (when dual-stream). */
  const handlePatrolMainFlatClick = useCallback(
    (e) => {
      if (
        e.target.closest(
          ".ds-preview-ptz-fs-overlay, .patrol-live-ptz-controls, .ds-ptz-btn, .ds-ptz-dpad, .ds-ptz-edge-pad",
        )
      ) {
        return;
      }
      if (patrolCanLensSwap && patrolMainLens === "ptz") {
        e.stopPropagation();
        swapPatrolMainLens();
        return;
      }
      void handlePlayPause();
    },
    [patrolCanLensSwap, patrolMainLens, swapPatrolMainLens, handlePlayPause],
  );

  /**
   * Handle previous video. When image viewer is visible, triggers slide then updates index on slide complete (same as auto).
   */
  const handlePrevious = useCallback(() => {
    if (currentVideoIndex <= 0) return;
    setIsPlaying(false);
    changeIndexType.current = "manual";
    if (showCircularProgressIcon) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    } else {
      setSlideDirection("prev");
    }
  }, [currentVideoIndex, videos, showCircularProgressIcon]);

  /**
   * Handle next video (with loop support). When image viewer is visible, triggers slide then updates index on slide complete (same as auto).
   */
  const handleNext = useCallback(() => {
    setIsPlaying(false);
    changeIndexType.current = "manual";
    if (showCircularProgressIcon) {
      if (currentVideoIndex < videos.length - 1) {
        setCurrentVideoIndex(currentVideoIndex + 1);
      } else {
        setCurrentVideoIndex(0);
      }
    } else {
      setSlideDirection("next");
    }
  }, [currentVideoIndex, videos, showCircularProgressIcon]);

  /** Marzipano-style link hotspot — advance to next pin along floor route. */
  const handlePatrolLinkHotspotClick = useCallback(() => {
    if (slideDirection != null || !linkHotspotTarget) return;
    if (
      linkHotspotTarget.videoIndex >= 0 &&
      linkHotspotTarget.videoIndex === currentVideoIndexRef.current
    ) {
      return;
    }

    setIsPlaying(false);
    changeIndexType.current = "manual";
    pausePatrolAutoRotation();

    if (showCircularProgressIcon) {
      if (linkHotspotTarget.videoIndex >= 0) {
        setCurrentVideoIndex(linkHotspotTarget.videoIndex);
      }
    } else {
      setSlideDirection("next");
    }
  }, [slideDirection, linkHotspotTarget, showCircularProgressIcon, pausePatrolAutoRotation]);

  /**
   * Called when RotatingCircle slide finishes (rotation complete or manual next/prev). Updates index so manual and auto behave the same.
   */
  const handleSlideComplete = useCallback(
    (direction) => {
      setIsRotationCycleComplete(false);
      setSlideDirection(null);
      const videosArray = videosRef.current;
      if (!Array.isArray(videosArray) || videosArray.length === 0) return;

      const pendingVideo = pendingSlideTargetVideoIndexRef.current;
      if (
        typeof pendingVideo === "number" &&
        pendingVideo >= 0 &&
        pendingVideo < videosArray.length
      ) {
        setCurrentVideoIndex(pendingVideo);
        pendingSlideTargetVideoIndexRef.current = null;
      } else if (direction === "next") {
        setCurrentVideoIndex((prev) =>
          prev >= videosArray.length - 1 ? 0 : prev + 1,
        );
      } else {
        setCurrentVideoIndex((prev) => (prev <= 0 ? 0 : prev - 1));
      }
      if (changeIndexType.current === "auto") {
        startPatrolAutoRotation();
      }
    },
    [startPatrolAutoRotation],
  );

  /** Jump to any clicked floor-plan pin index after smooth transition animation completes. */
  const handlePinTransitionComplete = useCallback(
    (targetPinIndex) => {
      setIsRotationCycleComplete(false);
      setSlideDirection(null);
      const videosArray = videosRef.current;
      if (!Array.isArray(videosArray) || videosArray.length === 0) return;
      if (!Number.isInteger(targetPinIndex)) return;

      const videoIndex = patrolVideoIndexForFloorPin(
        selectedPatrol,
        sortedFloorPins,
        targetPinIndex,
      );
      if (videoIndex < 0 || videoIndex >= videosArray.length) return;
      setCurrentVideoIndex(videoIndex);
    },
    [selectedPatrol, sortedFloorPins],
  );

  /**
   * Floor-plan slide aborted (e.g. zoom during auto-advance). Clear latch; resume 360 spin for same
   * segment when it was auto patrol — pin-click slides do not restart rotation here.
   */
  const handlePatrolFloorSlideAborted = useCallback(
    (detail) => {
      setIsRotationCycleComplete(false);
      setSlideDirection(null);
      if (detail?.fromPinClick || !detail?.fromRotationCycle) return;

      const rawYaw = rotationRef.current.yaw;
      const resumeYaw =
        rawYaw >= 360 ? 359.99 : Math.min(Math.max(rawYaw % 360, 0), 359.99);
      const elapsedOffset = (resumeYaw / 360) * rotationDurationMs;
      rotationStartTimeRef.current = performance.now() - elapsedOffset;
      lastStateUpdateTimeRef.current = performance.now();
      isManualControlRef.current = false;
      enablePatrolAutoRotationUi();
    },
    [rotationDurationMs, enablePatrolAutoRotationUi],
  );

  /**
   * Set view mode
   */
  const setViewModeHandler = useCallback(
    (mode) => {
      if (mode == viewMode) {
        return;
      }
      patrolSceneLayerRef.current?.style.setProperty("opacity", "1");
      setViewMode(mode);
      const nextFov = mode === "normal" || mode === "quad" ? 75 : null;
      if (nextFov != null) {
        targetFovRef.current = nextFov;
        currentSmoothFovRef.current = nextFov;
        rotationRef.current.fov = nextFov;
        setRotation((prev) => ({ ...prev, fov: nextFov }));
      }
    },
    [viewMode],
  );

  const togglePatrolView360 = useCallback(() => {
    setViewModeHandler(viewMode === "flat" ? "normal" : "flat");
  }, [viewMode, setViewModeHandler]);

  const togglePatrolPipPopup = useCallback(() => {
    setPatrolPipPopupHiddenState((hidden) => {
      const next = !hidden;
      setPipPopupHidden(next);
      return next;
    });
  }, []);

  /**
   * Handle quad view selection
   */
  const handleQuadViewSelect = useCallback((view) => {
    setSelectedQuadView(view);
    // Update main rotation to match selected view
    setQuadRotations((prev) => {
      const newRotations = {
        ...prev,
        main: { ...prev[view] },
      };
      quadRotationsRef.current = newRotations;
      return newRotations;
    });
  }, []);

  // Initialize and sync quad rotations ref with state
  useEffect(() => {
    quadRotationsRef.current = {
      front: { yaw: 0, pitch: 0 },
      rear: { yaw: 180, pitch: 0 },
      left: { yaw: -90, pitch: 0 },
      right: { yaw: 90, pitch: 0 },
      main: { yaw: 0, pitch: 0 },
    };
  }, []);

  useEffect(() => {
    quadRotationsRef.current = quadRotations;
  }, [quadRotations]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) {
        setViewerAspect(w / h);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewMode]);

  /**
   * Handle volume change
   */
  const handleVolumeChange = useCallback((event, newValue) => {
    const newVolume = Array.isArray(newValue) ? newValue[0] : newValue;
    const video = videoRef.current;
    if (video) {
      video.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  }, []);

  /**
   * Toggle mute/unmute
   */
  const handleToggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      // Unmute: restore previous volume or default to 0.7
      const newVolume = volume > 0 ? volume : 0.7;
      video.muted = false;
      video.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(false);
    } else {
      // Mute: save current volume and set to 0
      video.muted = true;
      video.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  /**
   * Toggle fullscreen mode
   */
  const handleToggleFullscreen = useCallback(() => {
    setIsImageViewerMinimized(true);
    setIsCircularProgressHovered(false);
    const container = playerContainerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, []);

  /**
   * Publish live preview toolbar actions to PatrolViewer header.
   */
  useEffect(() => {
    if (!onLivePreviewToolbarChange) return undefined;
    if (!liveMonitoring || !patrolLiveMainStreamUrl) {
      onLivePreviewToolbarChange(null);
      return undefined;
    }
    onLivePreviewToolbarChange({
      showView360Toggle: isPatrolLive360Main,
      viewModeFlat: viewMode === "flat",
      onToggleView360: togglePatrolView360,
      showStreamTransportToggle: true,
      activeStreamTransport: patrolLiveMainStreamTransport,
      mainStreamKind: patrolMainStreamKindLabel,
      onToggleStreamTransport: togglePatrolStreamTransport,
      showLensSwap: patrolCanLensSwap,
      onToggleLens: togglePatrolMainLens,
      showFullscreen: true,
      isFullscreen,
      onToggleFullscreen: handleToggleFullscreen,
      showPipToggle: true,
      pipHidden: patrolPipPopupHidden,
      onTogglePipHidden: togglePatrolPipPopup,
    });
    return () => onLivePreviewToolbarChange(null);
  }, [
    onLivePreviewToolbarChange,
    liveMonitoring,
    patrolCanLensSwap,
    isPatrolLive360Main,
    viewMode,
    togglePatrolView360,
    patrolLiveMainStreamUrl,
    patrolLiveMainStreamTransport,
    patrolMainStreamKindLabel,
    togglePatrolStreamTransport,
    togglePatrolMainLens,
    isFullscreen,
    handleToggleFullscreen,
    patrolPipPopupHidden,
    togglePatrolPipPopup,
  ]);

  /**
   * Handle menu open
   */
  const handleMenuOpen = useCallback((event) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  /**
   * Handle menu close
   */
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  /**
   * Handle view mode change from menu
   */
  const handleMenuViewModeChange = useCallback(
    (mode) => {
      setViewModeHandler(mode);
      handleMenuClose();
    },
    [setViewModeHandler, handleMenuClose],
  );

  /**
   * Handle fullscreen from menu
   */
  const handleMenuFullscreen = useCallback(() => {
    handleToggleFullscreen();
    handleMenuClose();
  }, [handleToggleFullscreen, handleMenuClose]);

  const handleMenuSwitchLens = useCallback(() => {
    togglePatrolMainLens();
    handleMenuClose();
  }, [togglePatrolMainLens, handleMenuClose]);

  /**
   * Handle play/pause from menu
   */
  const handleMenuPlayPause = useCallback(() => {
    handlePlayPause();
    handleMenuClose();
  }, [handlePlayPause, handleMenuClose]);

  const handleTogglePatrolEquirectAssetHotspots = useCallback((event) => {
    event.stopPropagation();
    const next = event.target.checked;
    setShowPatrolEquirectAssetHotspots(next);
    try {
      localStorage.setItem(
        PATROL_EQUIRECT_ASSET_HOTSPOTS_STORAGE_KEY,
        next ? "true" : "false",
      );
    } catch {
      /* ignore */
    }
  }, []);

  const patrolEquirectSettingsSwitchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#4CAF50" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#4CAF50",
    },
  };

  /**
   * Toggle auto-rotation for patrol.
   * Live dual-camera: if PTZ is main, swap 360° to main before rotating (not in PiP).
   */
  const handleToggleAutoRotate = useCallback(() => {
    if (autoRotate) {
      pausePatrolAutoRotation();
      return;
    }

    const ptzIsMain =
      liveMonitoring && patrolCanLensSwap && isPatrolLivePtzFlatMain;

    setPatrolFocusRingNorm(null);
    setIsRotationCycleComplete(false);

    if (ptzIsMain) {
      setPatrolMainLens("360");
    }

    setViewMode("normal");
    const resumeYawSource =
      lastYawBeforePauseRef.current != null
        ? lastYawBeforePauseRef.current
        : rotationRef.current.yaw;
    const rawYaw = resumeYawSource;
    const resumeYaw =
      rawYaw >= 360 ? 359.99 : Math.min(Math.max(rawYaw % 360, 0), 359.99);
    currentSmoothYawRef.current = resumeYaw;
    targetYawRef.current = resumeYaw;
    rotationRef.current.yaw = resumeYaw;
    setRotation((prev) => ({ ...prev, yaw: resumeYaw }));
    const elapsedOffset = (resumeYaw / 360) * rotationDurationMs;
    rotationStartTimeRef.current = performance.now() - elapsedOffset;
    rotationTimerRef.current = elapsedOffset;
    lastStateUpdateTimeRef.current = performance.now();

    enablePatrolAutoRotationUi();
  }, [
    autoRotate,
    liveMonitoring,
    patrolCanLensSwap,
    isPatrolLivePtzFlatMain,
    rotationDurationMs,
    pausePatrolAutoRotation,
    enablePatrolAutoRotationUi,
  ]);

  /**
   * Handle click on video screen to stop patrol
   */
  const handleVideoScreenClick = useCallback(
    (e) => {
      // Only stop patrol if clicking on the canvas/video container (not on controls)
      if (viewMode === "flat") return;
      // Don't stop if user is dragging
      if (isDraggingRef.current || isDraggingQuadRef.current) {
        return;
      }
      // Check if click is on a control element
      if (e.target.closest("button") || e.target.closest('[role="button"]')) {
        return;
      }
      if (liveMonitoring && patrolCanLensSwap) {
        revealPatrolLivePtzPip();
      }
      if (autoRotate) {
        pausePatrolAutoRotation();
      }
    },
    [
      viewMode,
      autoRotate,
      liveMonitoring,
      patrolCanLensSwap,
      revealPatrolLivePtzPip,
      pausePatrolAutoRotation,
    ],
  );

  // Initialize renderer when mode changes or video loads
  useLayoutEffect(() => {
    if (viewMode === "normal") {
      let cancelled = false;
      const boot = () => {
        if (cancelled || !videoRef.current || !canvasRef.current) return;
        const hasRenderer = Boolean(rendererRef.current && sceneRef.current);
        initMain360Renderer(hasRenderer);
      };
      const frameId = requestAnimationFrame(() => {
        requestAnimationFrame(boot);
      });

      return () => {
        cancelled = true;
        cancelAnimationFrame(frameId);
        detachMain360WebGLResources();
      };
    }

    if (viewMode === "quad") {
      let cancelled = false;
      const timer = setTimeout(() => {
        if (!cancelled && videoRef.current) {
          initQuadViewRenderers();
        }
      }, 0);

      return () => {
        cancelled = true;
        clearTimeout(timer);
        Object.keys(quadResizeRafRefs.current).forEach((view) => {
          const rafId = quadResizeRafRefs.current[view];
          if (rafId) {
            cancelAnimationFrame(rafId);
            quadResizeRafRefs.current[view] = 0;
          }
        });
        Object.keys(quadResizeObserversRef.current).forEach((view) => {
          const obs = quadResizeObserversRef.current[view];
          if (obs) {
            obs.disconnect();
            quadResizeObserversRef.current[view] = null;
          }
        });
        Object.keys(quadAnimationFrameRefs.current).forEach((view) => {
          if (quadAnimationFrameRefs.current[view]) {
            cancelAnimationFrame(quadAnimationFrameRefs.current[view]);
            quadAnimationFrameRefs.current[view] = null;
          }
        });
        Object.keys(quadRendererRefs.current).forEach((view) => {
          if (quadRendererRefs.current[view]) {
            quadRendererRefs.current[view].dispose();
            quadRendererRefs.current[view] = null;
          }
        });
      };
    }

    disposeMain360Renderer();
    return undefined;
  }, [
    viewMode,
    initMain360Renderer,
    initQuadViewRenderers,
    detachMain360WebGLResources,
    disposeMain360Renderer,
  ]);

  /** Live stream (HLS / WebRTC — same relay as wankhede-360). */
  useEffect(() => {
    if (!patrolLiveMainStreamUrl) {
      if (liveHlsRef.current) {
        liveHlsRef.current.destroy();
        liveHlsRef.current = null;
      }
      return undefined;
    }

    const video = videoRef.current;
    if (!video) return undefined;

    const src = resolveOmniMediaUrl(patrolLiveMainStreamUrl);
    if (!src) return undefined;

    let cancelled = false;
    const abort = new AbortController();

    (async () => {
      try {
        setError(null);
        if (liveHlsRef.current) {
          liveHlsRef.current.destroy();
          liveHlsRef.current = null;
        }

        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = "anonymous";

        const waitForFrames = () =>
          new Promise((resolve, reject) => {
            const onReady = () => {
              video.removeEventListener("loadeddata", onReady);
              video.removeEventListener("playing", onReady);
              video.removeEventListener("error", onErr);
              resolve();
            };
            const onErr = () => {
              video.removeEventListener("loadeddata", onReady);
              video.removeEventListener("playing", onReady);
              video.removeEventListener("error", onErr);
              reject(new Error("Live stream failed to load."));
            };
            if (video.readyState >= 2) {
              resolve();
              return;
            }
            video.addEventListener("loadeddata", onReady);
            video.addEventListener("playing", onReady);
            video.addEventListener("error", onErr);
          });

        const session = attachLivePlayback({
          playlistUrl: src,
          streamType: patrolLiveMainStreamTransport,
          video,
          signal: abort.signal,
          onFatalError: () => {
            if (!cancelled) {
              throw new Error("Live stream error.");
            }
          },
        });

        if (!session) {
          throw new Error("Could not start live stream playback.");
        }

        await video.play().catch(() => undefined);
        await waitForFrames();

        if (!cancelled) {
          setIsPlaying(true);
          setPatrolSceneVisible(true);
          patrolCrossfadePrimedRef.current = true;
          if (isPatrolLivePtzFlatMain) {
            setViewMode("flat");
            pausePatrolAutoRotation();
          } else {
            initMain360Renderer();
            if (autoPlay) {
              enablePatrolAutoRotationUi();
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Live stream attach failed:", err);
          setError(
            err instanceof Error
              ? err.message
              : "Could not connect to the live camera stream.",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
      if (liveHlsRef.current) {
        liveHlsRef.current.destroy();
        liveHlsRef.current = null;
      }
    };
  }, [
    patrolLiveMainStreamUrl,
    patrolLiveMainStreamTransport,
    currentVideoIndex,
    patrolMainLens,
    isPatrolLivePtzFlatMain,
    initMain360Renderer,
    autoPlay,
    enablePatrolAutoRotationUi,
    pausePatrolAutoRotation,
  ]);

  // Load video when index changes or when videos array changes
  useEffect(() => {
    if (patrolLiveMainStreamUrl) return;
    if (videos && videos.length > 0 && currentVideoIndex < videos.length) {
      if (skipNextPatrolLoadVideoEffectRef.current) {
        skipNextPatrolLoadVideoEffectRef.current = false;
        return;
      }
      const url = videos[currentVideoIndex];
      if (!url) return;
      loadVideo(currentVideoIndex);
    }
  }, [currentVideoIndex, videos, loadVideo, patrolLiveMainStreamUrl]);

  // Sync volume with video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = isMuted;
    video.volume = isMuted ? 0 : volume;

    const cross = patrolCrossfadeVideoRef.current;
    if (cross) {
      cross.muted = isMuted;
      cross.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Sync yaw/pitch from React state; FOV is driven by targetFovRef + render-loop lerp.
  useEffect(() => {
    if (
      Math.abs(rotationRef.current.yaw - rotation.yaw) > 0.1 ||
      Math.abs(rotationRef.current.pitch - rotation.pitch) > 0.1
    ) {
      rotationRef.current.yaw = rotation.yaw;
      rotationRef.current.pitch = rotation.pitch;
    }
  }, [rotation.yaw, rotation.pitch]);

  // Keep videos ref in sync
  useEffect(() => {
    videosRef.current = videos;
  }, [videos]);

  // Keep currentVideoIndex ref in sync
  useEffect(() => {
    currentVideoIndexRef.current = currentVideoIndex;
  }, [currentVideoIndex]);

  /** Measure main video stage so PTZ PiP size tracks settings (% of main). */
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setPatrolVideoStageRect({
        width: r.width,
        height: r.height,
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFullscreen, currentVideoIndex, viewMode]);

  /**
   * When the floor plan cannot slide (hidden / single pin) or only the circular
   * progress overlay is shown, advance and/or restart without PatrolImageViewer.
   */
  useEffect(() => {
    if (!isRotationCycleComplete) return;

    const videosArray = videosRef.current;
    if (!Array.isArray(videosArray) || videosArray.length === 0) return;

    const canFloorSlide =
      Boolean(imageUrl) &&
      sortedFloorPins.length > 1 &&
      !showCircularProgressIcon;

    if (canFloorSlide) return;

    setIsRotationCycleComplete(false);

    if (showCircularProgressIcon) {
      const currentIdx = currentVideoIndexRef.current;
      if (currentIdx < videosArray.length - 1) {
        setCurrentVideoIndex((prev) => prev + 1);
      } else {
        setCurrentVideoIndex(0);
      }
    }

    if (changeIndexType.current === "auto") {
      startPatrolAutoRotation();
    }
  }, [
    isRotationCycleComplete,
    showCircularProgressIcon,
    imageUrl,
    sortedFloorPins.length,
    startPatrolAutoRotation,
  ]);

  // Setup mouse event listeners for 360 modes
  useEffect(() => {
    if (viewMode === "flat") return;

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    if (viewMode !== "quad") {
      container.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("wheel", handleWheel);
    };
  }, [viewMode, handleMouseDown, handleMouseMove, handleMouseUp, handleWheel]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = async () => {
      setIsPlaying(false);

      if (video) {
        video.currentTime = 0;
        try {
          await video.play();
          setIsPlaying(true);
        } catch (err) {
          console.error("Error replaying video:", err);
        }
      }
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [currentVideoIndex, videos]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      if (!isCurrentlyFullscreen) {
        setIsImageViewerMinimized(true);
        setShowCircularProgressIcon(false);
        setIsCircularProgressHovered(false);
        setIsPictureInPictureCircleHovered(false);
      }
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
    };
  }, []);

  useEffect(() => {
    if (!showCircularProgressIcon) {
      setIsCircularProgressHovered(false);
    }
  }, [showCircularProgressIcon]);

  // Reset currentVideoIndex only when patrol media URLs actually change (not on new `videos` array identity)
  useEffect(() => {
    const currentLength = videos?.length || 0;
    const sig = currentLength ? videos.join("\u0001") : "";

    if (patrolVideosSignatureRef.current === sig) {
      return;
    }
    patrolVideosSignatureRef.current = sig;

    setCurrentVideoIndex(0);
    patrolCrossfadePrimedRef.current = false;
    skipNextPatrolLoadVideoEffectRef.current = false;
    setPatrolSceneVisible(true);
    requestAnimationFrame(() => {
      patrolSceneLayerRef.current?.style.setProperty("opacity", "1");
    });

    if (currentLength !== previousVideosLengthRef.current) {
      isResettingRef.current = true;
      previousVideosLengthRef.current = currentLength;

      requestAnimationFrame(() => {
        isResettingRef.current = false;
      });
    }
  }, [videos]);

  useEffect(() => {
    const updateControlsWidth = () => {
      if (controlsContainerRef.current) {
        setControlsContainerWidth(controlsContainerRef.current.offsetWidth);
      }
    };

    updateControlsWidth();
    const resizeObserver = new ResizeObserver(updateControlsWidth);

    if (controlsContainerRef.current) {
      resizeObserver.observe(controlsContainerRef.current);
    }

    window.addEventListener("resize", updateControlsWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateControlsWidth);
    };
  }, []);

  // Calculate currentPinId based on currentVideoIndex and selectedPatrol
  useEffect(() => {
    if (
      selectedPatrol &&
      selectedPatrol.length > 0 &&
      currentVideoIndex >= 0 &&
      currentVideoIndex < selectedPatrol.length
    ) {
      const currentPatrol = selectedPatrol[currentVideoIndex];
      if (currentPatrol && currentPatrol.pin_id) {
        setCurrentPinId(currentPatrol.pin_id);
      } else {
        setCurrentPinId(null);
      }
    } else {
      setCurrentPinId(null);
    }
  }, [currentVideoIndex, selectedPatrol]);
  useEffect(() => {
    counter.current = 0;
    changeIndexType.current = null;
    setIsRotationCycleComplete(false);

    if (autoPlay) {
      enablePatrolAutoRotationUi();
    } else {
      pausePatrolAutoRotation();
    }

    if (imageUrl) {
      setIsImageViewerMinimized(true);
      setShowCircularProgressIcon(false);
    }
  }, [autoPlay, videos, imageUrl, enablePatrolAutoRotationUi, pausePatrolAutoRotation]);

  if (liveMonitoringLoading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          color: "#6B7280",
        }}
      >
        <CircularProgress size={40} />
        <Typography sx={{ fontSize: 14 }}>
          Loading patrol drawing and cameras…
        </Typography>
      </Box>
    );
  }

  const hasRecordedVideos =
    Array.isArray(videos) &&
    videos.length > 0 &&
    videos.some((u) => Boolean(u) && !/^rtsp:/i.test(String(u)));

  const hasLivePatrolRows =
    liveMonitoring && Array.isArray(selectedPatrol) && selectedPatrol.length > 0;

  if (
    !hasRecordedVideos &&
    !patrolLiveMainStreamUrl &&
    !hasLivePatrolRows &&
    !imageUrl
  ) {
    if (liveMonitoring) {
      return <PatrolLiveMonitoringEmptyState />;
    }
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6B7280",
          px: 2,
          textAlign: "center",
        }}
      >
        No videos available
      </Box>
    );
  }

  /** When the floor plan is docked left, keep video/chrome in the right 60% (same as legacy flex split) so the 360 player is not centered under the drawing. */
  const floorPlanDocked =
    Boolean(imageUrl) && !showCircularProgressIcon && !isImageViewerMinimized;

  return (
    <Box
      ref={playerContainerRef}
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        position: "relative",
        bgcolor: "#000",
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          width: "100%",
        }}
      >
        {/* Video fills the player; floor plan is overlaid so width never toggles 60%/100% (no horizontal jump) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              position: "relative",
              width: floorPlanDocked ? "60%" : "100%",
              maxWidth: floorPlanDocked ? "60%" : "100%",
              marginLeft: floorPlanDocked ? "40%" : 0,
              alignSelf: "stretch",
              boxSizing: "border-box",
            }}
          >
            {/* Circular Progress Icon - Bottom Left */}
            {showCircularProgressIcon && (
              <Box
                sx={{
                  position: "absolute",
                  left: 16,
                  bottom: 80,
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                onMouseEnter={() => setIsCircularProgressHovered(true)}
                onMouseLeave={() => setIsCircularProgressHovered(false)}
              >
                <CircularProgressIcon
                  sx={{ fontSize: 10 }}
                  yaw={rotation.yaw}
                  fov={rotation.fov}
                />
                {/* Toggle Icons - Show on Hover */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    opacity: isCircularProgressHovered ? 1 : 0,
                    transition: "opacity 0.2s",
                    pointerEvents: isCircularProgressHovered ? "auto" : "none",
                  }}
                >
                  <IconButton
                    onClick={() => {
                      setIsImageViewerMinimized(true);
                      setShowCircularProgressIcon(false);
                      setIsCircularProgressHovered(false);
                      setIsPictureInPictureCircleHovered(false);
                    }}
                    onMouseEnter={() => setIsPictureInPictureHovered(true)}
                    onMouseLeave={() => setIsPictureInPictureHovered(false)}
                    sx={{
                      color: "#fff",
                    }}
                    title="Expand Image Viewer"
                  >
                    <PictureInPictureIcon
                      sx={{ fontSize: 24, color: "#fff" }}
                      fillColor={isPictureInPictureHovered ? "#1BC800" : "none"}
                    />
                  </IconButton>
                  {(showLiveMonitoringFloorControls || isFullscreen) && (
                    <IconButton
                      onClick={() => {
                        setIsImageViewerMinimized(false);
                        setShowCircularProgressIcon(false);
                        setIsCircularProgressHovered(false);
                        setIsPictureInMaxPictureHovered(false);
                      }}
                      onMouseEnter={() => setIsPictureInMaxPictureHovered(true)}
                      onMouseLeave={() =>
                        setIsPictureInMaxPictureHovered(false)
                      }
                      title="Maximize Floor Plan"
                    >
                      <PictureInMaxPictureIcon
                        sx={{ fontSize: 24 }}
                        fillColor={
                          isPictureInMaxPictureHovered ? "#1BC800" : "none"
                        }
                      />
                    </IconButton>
                  )}
                </Box>
              </Box>
            )}
            {/* Volume Control - Top Right Corner */}
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 16,
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                bgcolor: "rgba(0, 0, 0, 0.6)",
                borderRadius: 2,
                p: 1,
                maxWidth: "calc(100% - 32px)",
              }}
              onMouseEnter={() => setShowVolumeSlider(true)}
              onMouseLeave={() => setShowVolumeSlider(false)}
            >
              <IconButton
                onClick={handleToggleMute}
                sx={{
                  color: "#fff",
                }}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              {showVolumeSlider && (
                <Slider
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  min={0}
                  max={1}
                  step={0.01}
                  orientation="vertical"
                  sx={{
                    height: 100,
                    color: "#fff",
                    "& .MuiSlider-thumb": {
                      color: "#fff",
                    },
                    "& .MuiSlider-track": {
                      color: "#fff",
                    },
                    "& .MuiSlider-rail": {
                      color: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                />
              )}
            </Box>

            {/* Video Container */}
            <Box
              ref={containerRef}
              onClick={handleVideoScreenClick}
              sx={{
                flex: 1,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                cursor:
                  viewMode !== "flat"
                    ? isDraggingRef.current || isDraggingQuadRef.current
                      ? "grabbing"
                      : "grab"
                    : "default",
              }}
            >
              <Box
                ref={patrolSceneLayerRef}
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                  bgcolor: "transparent",
                  pointerEvents: patrolSceneVisible ? "auto" : "none",
                  WebkitBackfaceVisibility: "hidden",
                  backfaceVisibility: "hidden",
                }}
              >
                {/* Persistent video element - always rendered to maintain state */}
                <Box
                  component="video"
                  ref={patrolCrossfadeVideoRef}
                  sx={{ display: "none", position: "absolute", width: 0, height: 0 }}
                  crossOrigin="anonymous"
                  playsInline
                  muted
                />

                <Box
                  component="video"
                  ref={videoRef}
                  sx={{
                    display: viewMode === "flat" ? "block" : "none",
                    ...(viewMode === "flat" && {
                      width: isPatrolLivePtzFlatMain ? "100%" : "auto",
                      height: isPatrolLivePtzFlatMain ? "100%" : "auto",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: isPatrolLivePtzFlatMain ? "cover" : "contain",
                      transform: "translateZ(0)",
                      backfaceVisibility: "hidden",
                    }),
                  }}
                  onClick={
                    viewMode === "flat" ? handlePatrolMainFlatClick : undefined
                  }
                  crossOrigin="anonymous"
                />

                {viewMode === "quad" && (
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      height: "100%",
                      display: "flex",
                      minWidth: 0,
                    }}
                  >
                    {/* Left side - 4 mini views */}
                    <Box
                      data-quad-mini-view="true"
                      sx={{
                        width: "30%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 1,
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      {["front", "rear", "left", "right"].map((view) => (
                        <Box
                          key={view}
                          data-quad-mini-view="true"
                          onClick={() => handleQuadViewSelect(view)}
                          sx={{
                            flex: 1,
                            position: "relative",
                            border:
                              selectedQuadView === view
                                ? "2px solid #4CAF50"
                                : "2px solid transparent",
                            borderRadius: 1,
                            cursor: "pointer",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            component="canvas"
                            ref={(el) => (quadCanvasRefs.current[view] = el)}
                            sx={{
                              width: "100%",
                              maxWidth: "100%",
                              height: "100%",
                              display: "block",
                              minWidth: 0,
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              left: 4,
                              color: "#fff",
                              fontSize: "10px",
                              textTransform: "capitalize",
                              bgcolor: "rgba(0, 0, 0, 0.6)",
                              px: 0.5,
                              borderRadius: 0.5,
                            }}
                          >
                            {view}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    {/* Right side - main rotatable view */}
                    <Box
                      sx={{
                        flex: 1,
                        position: "relative",
                      }}
                    >
                      <Box
                        component="canvas"
                        ref={(el) => (quadCanvasRefs.current.main = el)}
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          height: "100%",
                          display: "block",
                          minWidth: 0,
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {viewMode !== "flat" && (
                  <Box
                    component="canvas"
                    ref={canvasRef}
                    sx={{
                      position: viewMode === "quad" ? "absolute" : "relative",
                      inset: viewMode === "quad" ? 0 : undefined,
                      width: "100%",
                      maxWidth: "100%",
                      height: "100%",
                      display: viewMode === "normal" ? "block" : "none",
                      minWidth: 0,
                      pointerEvents: viewMode === "normal" ? "auto" : "none",
                    }}
                  />
                )}

                <PatrolPanoramaFocusRing
                  focusNorm={patrolFocusRingNorm}
                  enabled={
                    patrolPanoramaPickingEnabled &&
                    viewMode === "normal" &&
                    !patrolLivePtzAim.usesPresetGridPanoramaPick
                  }
                  canvasRef={canvasRef}
                  cameraRef={cameraRef}
                  hostRef={patrolSceneLayerRef}
                  viewerRotation={rotation}
                  ptzZoom={patrolLivePtzAim.ptz.zoom}
                  ptzZoomMin={patrolLivePtzAim.ptzZoomSliderMin}
                  ptzZoomMax={patrolLivePtzAim.ptzZoomSliderMax}
                />

                <PatrolPresetGridOverlay
                  enabled={
                    liveMonitoring &&
                    isPatrolLive360Main &&
                    viewMode === "normal" &&
                    patrolLivePtzAim.usesPresetGridPanoramaPick &&
                    patrolLivePtzAim.ptzOperable
                  }
                  canvasRef={canvasRef}
                  cameraRef={cameraRef}
                  hostRef={patrolSceneLayerRef}
                  viewerRotation={rotation}
                  mappedPresetGridIds={patrolLivePtzAim.mappedPresetGridIds}
                  selectedPresetGridId={patrolLivePtzAim.selectedPresetGridId}
                  onPresetGridSelect={onPatrolPresetGridSelect}
                />

                <PatrolAssetHotspotsLayer
                  enabled={showPatrolAssetHotspotsLayer}
                  hotspots={patrolAssetHotspots}
                  canvasRef={canvasRef}
                  cameraRef={cameraRef}
                  hostRef={patrolSceneLayerRef}
                />
              </Box>

              {showPatrolLinkHotspot && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 9,
                    pointerEvents: "none",
                    overflow: "hidden",
                  }}
                >
                  <PatrolLinkHotspot
                    hotspotYawDeg={linkHotspotBearingDeg}
                    viewerYawDeg={rotation.yaw}
                    viewerPitchDeg={rotation.pitch}
                    verticalFovDeg={rotation.fov}
                    viewerAspect={viewerAspect}
                    label={linkHotspotTarget.label}
                    onClick={handlePatrolLinkHotspotClick}
                    disabled={slideDirection != null}
                  />
                </Box>
              )}

              {patrolLivePipReady && patrolLivePipStreamUrl ? (
                <div
                  className={`ds-preview-pip patrol-live-ptz-pip${
                    patrolCanLensSwap ? " ds-preview-pip--swappable" : ""
                  }${patrolLivePipShown ? "" : " patrol-live-ptz-pip--suppressed"}`}
                  style={{
                    width: patrolPtzPreviewPx.width,
                    height: patrolPtzPreviewPx.height,
                    left: 16,
                    top: 16,
                    right: "auto",
                    bottom: "auto",
                  }}
                  title={
                    patrolCanLensSwap ? "Tap to switch camera" : undefined
                  }
                  onPointerDown={
                    patrolCanLensSwap && patrolLivePipShown
                      ? onPatrolPipPointerDown
                      : undefined
                  }
                  onPointerUp={
                    patrolCanLensSwap && patrolLivePipShown
                      ? onPatrolPipPointerUp
                      : undefined
                  }
                  onPointerCancel={
                    patrolCanLensSwap && patrolLivePipShown
                      ? onPatrolPipPointerUp
                      : undefined
                  }
                  onClick={
                    patrolCanLensSwap && patrolLivePipShown
                      ? handlePatrolPipSwapClick
                      : undefined
                  }
                >
                  <div className="ds-preview-pip__player">
                    {patrolCanLensSwap ? (
                      <div
                        className="ds-preview-pip__chrome ds-preview-pip__chrome--overlay"
                        aria-hidden
                      >
                        <span>{patrolPipLensLabel}</span>
                      </div>
                    ) : null}
                    <div
                      className={`ds-preview-pip-flat-surface${
                        patrolCanLensSwap
                          ? " ds-preview-pip-flat-surface--swappable"
                          : ""
                      }`}
                      role="region"
                      aria-label={
                        patrolMainLens === "360"
                          ? "PTZ live preview — tap to show on main view"
                          : "360° live preview — tap to show on main view"
                      }
                    >
                      <div className="ds-preview-pip-flat-surface__media">
                        {patrolMainLens === "ptz" && live360StreamUrl ? (
                          <PatrolPip360LivePreview
                            key={`patrol-pip-360-${currentVideoIndex}-${live360StreamUrl}-${stream360Transport}`}
                            playlistUrl={live360StreamUrl}
                            streamType={stream360Transport}
                            autoRotate={autoRotate}
                            rotationDurationSeconds={rotationDurationSeconds}
                            onTapToSwap={swapPatrolMainLens}
                          />
                        ) : (
                          <StreamVideo
                            key={`patrol-live-pip-${currentVideoIndex}-${patrolMainLens}-${patrolLivePipStreamUrl}-${patrolLivePipStreamTransport}`}
                            playlistUrl={patrolLivePipStreamUrl}
                            streamType={patrolLivePipStreamTransport}
                            objectFit="cover"
                            enabled
                            onStreamError={() => {}}
                          />
                        )}
                      </div>
                    </div>
                    {patrolCanLensSwap && patrolLivePipShown ? (
                      <button
                        type="button"
                        className="patrol-pip-swap-layer patrol-pip-swap-hit ds-preview-pip__swap-hit"
                        aria-label="Switch camera — show PTZ on main view"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          triggerPatrolPipSwap();
                        }}
                      />
                    ) : null}
                    {showPatrolPtzControlsOnPip ? (
                      <div className="patrol-live-ptz-pip__controls-wrap">
                        <PatrolLivePtzControls
                          key={`patrol-ptz-pip-ctrl-${currentVideoIndex}-${livePtzCameraId}`}
                          cameraId={livePtzCameraId}
                          enabled
                          variant="pip"
                          vendor={liveActiveRecording?.camera_vendor}
                          sharedControl={patrolLivePtzAim}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {showPatrolPtzControlsOnMain ? (
                <PatrolLivePtzControls
                  key={`patrol-ptz-main-ctrl-${currentVideoIndex}-${livePtzCameraId}`}
                  cameraId={livePtzCameraId}
                  enabled
                  variant="fullscreen"
                  vendor={liveActiveRecording?.camera_vendor}
                  sharedControl={patrolLivePtzAim}
                />
              ) : null}

              {/* Error message */}
              {error && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "#ff4444",
                    textAlign: "center",
                    p: 2,
                  }}
                >
                  {error}
                </Box>
              )}
            </Box>

            {/* Controls */}
            <Box
              ref={controlsContainerRef}
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.8)",
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexShrink: 0,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {/* Left spacer - balances right side so center stays centered */}
              <Box sx={{ flex: 1, minWidth: 0 }} />
              {/* Center - Playback controls */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 1, sm: 2 },
                  minWidth: 0,
                  flexShrink: 0,
                  overflow: "hidden",
                  pr: 1,
                }}
              >
                {/* Previous / rotate / next — main toolbar only */}
                {showController && showPatrolPlayerChromeInToolbar ? (
                  <IconButton
                    onClick={handlePrevious}
                    disabled={currentVideoIndex === 0}
                    sx={{
                      color: "#fff",
                      flexShrink: 0,
                      "&:disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <SkipPreviousIcon />
                  </IconButton>
                ) : null}

                {showPatrolAutoRotateInToolbar ? (
                  <IconButton
                    onClick={handleToggleAutoRotate}
                    disabled={!!error || !patrolSceneVisible}
                    sx={{
                      color: autoRotate ? "#4CAF50" : "#fff",
                      position: "relative",
                      flexShrink: 0,
                      "&:disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                    title={
                      autoRotate
                        ? "Pause Auto Rotation (Patrol)"
                        : "Start Auto Rotation (Patrol)"
                    }
                  >
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <SyncIcon sx={{ fontSize: "1.5em" }} />
                      {autoRotate ? (
                        <PauseIcon
                          sx={{
                            position: "absolute",
                            fontSize: "0.6em",
                            bgcolor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: "50%",
                            p: 0.2,
                          }}
                        />
                      ) : (
                        <PlayArrowIcon
                          sx={{
                            position: "absolute",
                            fontSize: "0.6em",
                            bgcolor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: "50%",
                            p: 0.2,
                            ml: 0.1,
                          }}
                        />
                      )}
                    </Box>
                  </IconButton>
                ) : null}

                {showController && showPatrolPlayerChromeInToolbar ? (
                  <IconButton
                    onClick={handleNext}
                    disabled={videos.length === 0}
                    sx={{
                      color: "#fff",
                      flexShrink: 0,
                      "&:disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <SkipNextIcon />
                  </IconButton>
                ) : null}

                {/* Video Counter */}
                {/* <Box
              sx={{
                color: "#fff",
                fontSize: "14px",
                ml: { xs: 1, sm: 2 },
                px: { xs: 1, sm: 2 },
                display:
                  controlsContainerWidth > 0 && controlsContainerWidth < 500
                    ? "none"
                    : "block",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {currentVideoIndex + 1} / {videos.length}
            </Box> */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ml: { xs: 1, sm: 2 },
                    borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
                    pl: { xs: 1, sm: 2 },
                    flexShrink: 0,
                  }}
                ></Box>
              </Box>

              {/* Right side — view modes & settings in ⋮ menu */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{
                    color: "#fff",
                    flexShrink: 0,
                    minWidth: 40,
                  }}
                  title="View options"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      bgcolor: "rgba(0, 0, 0, 0.9)",
                      color: "#fff",
                      minWidth: 220,
                    },
                  }}
                >
                  {showPatrolViewModeMenu ? (
                    <>
                      <MenuItem
                        onClick={() => handleMenuViewModeChange("normal")}
                        selected={viewMode === "normal"}
                      >
                        <ListItemIcon>
                          <ThreeDRotationIcon
                            sx={{
                              color:
                                viewMode === "normal" ? "#4CAF50" : "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText>First Person View</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleMenuViewModeChange("quad")}
                        selected={viewMode === "quad"}
                      >
                        <ListItemIcon>
                          <ViewSidebarIcon
                            sx={{
                              rotate: "180deg",
                              color: viewMode === "quad" ? "#4CAF50" : "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText>Quad View</ListItemText>
                      </MenuItem>
                      <Divider
                        sx={{ borderColor: "rgba(255,255,255,0.12)" }}
                      />
                      <ListSubheader
                        disableSticky
                        sx={{
                          color: "rgba(255,255,255,0.55)",
                          fontSize: 11,
                          lineHeight: "28px",
                        }}
                      >
                        Equirectangular
                      </ListSubheader>
                      <MenuItem
                        onClick={() => handleMenuViewModeChange("flat")}
                        selected={viewMode === "flat"}
                      >
                        <ListItemIcon>
                          <PanoramaHorizontalSelectIcon
                            sx={{
                              color: viewMode === "flat" ? "#4CAF50" : "#fff",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText>Equirectangular View</ListItemText>
                      </MenuItem>
                      <MenuItem
                        dense
                        onClick={(e) => e.stopPropagation()}
                        sx={{ py: 0.25 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Box
                            component="img"
                            src={PATROL_HOTSPOT_LAYER_TOGGLE_ICON}
                            alt=""
                            aria-hidden
                            sx={{
                              width: 22,
                              height: 22,
                              objectFit: "contain",
                              opacity: showPatrolEquirectAssetHotspots ? 1 : 0.85,
                              filter: showPatrolEquirectAssetHotspots
                                ? "drop-shadow(0 0 4px rgba(76,175,80,0.9))"
                                : "none",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Asset hotspots"
                          secondary="Show in 360° view"
                          primaryTypographyProps={{ fontSize: 14 }}
                          secondaryTypographyProps={{
                            fontSize: 11,
                            color: "rgba(255,255,255,0.55)",
                          }}
                        />
                        <Switch
                          edge="end"
                          size="small"
                          checked={showPatrolEquirectAssetHotspots}
                          onChange={handleTogglePatrolEquirectAssetHotspots}
                          onClick={(e) => e.stopPropagation()}
                          sx={patrolEquirectSettingsSwitchSx}
                        />
                      </MenuItem>
                      <Divider
                        sx={{ borderColor: "rgba(255,255,255,0.12)" }}
                      />
                    </>
                  ) : null}
                  {patrolCanLensSwap ? (
                    <MenuItem onClick={handleMenuSwitchLens}>
                      <ListItemIcon>
                        <FlipCameraAndroidIcon sx={{ color: "#fff" }} />
                      </ListItemIcon>
                      <ListItemText>
                        {patrolMainLens === "360"
                          ? "Switch to PTZ (main)"
                          : "Switch to 360° (main)"}
                      </ListItemText>
                    </MenuItem>
                  ) : null}
                  <MenuItem onClick={handleMenuFullscreen}>
                    <ListItemIcon>
                      {isFullscreen ? (
                        <FullscreenExitIcon sx={{ color: "#fff" }} />
                      ) : (
                        <FullscreenIcon sx={{ color: "#fff" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText>
                      {isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Floor plan: overlays video (same z-stack) — no flex 40/60 split, so the 360 view does not slide right */}
        {imageUrl && !showCircularProgressIcon && (
          <>
            {isImageViewerMinimized ? (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 80,
                  left: 16,
                  width: "300px",
                  height: "200px",
                  zIndex: 4,
                  bgcolor: "rgba(0, 0, 0, 0.8)",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  flexDirection: "column",
                  transition: "opacity 0.2s ease, transform 0.2s ease",
                }}
              >
                <IconButton
                  onClick={() => {
                    setShowCircularProgressIcon(!showCircularProgressIcon);
                    setIsPictureInPictureCircleHovered(false);
                    setIsPictureInMaxPictureHovered(false);
                    setIsPictureInPictureHovered(false);
                  }}
                  onMouseEnter={() => setIsPictureInPictureCircleHovered(true)}
                  onMouseLeave={() => setIsPictureInPictureCircleHovered(false)}
                  sx={{
                    position: "absolute",
                    right: 4,
                    top: "10%",
                    transform: "translateY(-50%)",
                    zIndex: 5,
                    bgcolor: "rgba(0, 0, 0, 0.7)",
                    color: "#fff",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.9)",
                    },
                  }}
                  title="Close Floor Plan"
                >
                  <PictureInPictureCircleIcon
                    sx={{ fontSize: 20 }}
                    fillColor={
                      isPictureInPictureCircleHovered ? "#1BC800" : "none"
                    }
                  />
                </IconButton>
                {(showLiveMonitoringFloorControls || isFullscreen) && (
                  <IconButton
                    onClick={() => {
                      setIsImageViewerMinimized(false);
                      setIsPictureInMaxPictureHovered(false);
                      setIsPictureInPictureHovered(false);
                    }}
                    onMouseEnter={() => setIsPictureInMaxPictureHovered(true)}
                    onMouseLeave={() => setIsPictureInMaxPictureHovered(false)}
                    sx={{
                      position: "absolute",
                      right: 50,
                      top: "10%",
                      transform: "translateY(-50%)",
                      zIndex: 5,
                      bgcolor: "rgba(0, 0, 0, 0.7)",
                      color: "#fff",
                      width: 32,
                      height: 32,
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.9)",
                      },
                    }}
                    title="Maximize Floor Plan"
                  >
                    <PictureInMaxPictureIcon
                      sx={{ fontSize: 20 }}
                      fillColor={
                        isPictureInMaxPictureHovered ? "#1BC800" : "none"
                      }
                    />
                  </IconButton>
                )}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <PatrolImageViewer
                    imageUrl={imageUrl}
                    selectedPatrol={selectedPatrol}
                    onClose={() => setIsImageViewerMinimized(true)}
                    currentPinId={currentPinId}
                    isImageViewerMinimized={isImageViewerMinimized}
                    yaw={rotation.yaw}
                    fov={rotation.fov}
                    rotationCycleComplete={isRotationCycleComplete}
                    slideDirection={slideDirection}
                    onSlideComplete={handleSlideComplete}
                    onPinTransitionComplete={handlePinTransitionComplete}
                    onPatrolSlideAborted={handlePatrolFloorSlideAborted}
                    onPatrolFloorSlideStart={handlePatrolFloorSlideStart}
                    enableZoom={!isImageViewerMinimized}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "40%",
                  height: "100%",
                  zIndex: 4,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  bgcolor: "gray",
                  borderRight: "2px solid rgba(255, 255, 255, 0.2)",
                  pointerEvents: "auto",
                  boxSizing: "border-box",
                  transition: "opacity 0.2s ease, transform 0.25s ease",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    zIndex: 5,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    onClick={() => {
                      setShowCircularProgressIcon(!showCircularProgressIcon);
                      setIsPictureInMaxPictureHovered(false);
                      setIsPictureInPictureCircleHovered(false);
                    }}
                    onMouseEnter={() =>
                      setIsPictureInPictureCircleHovered(true)
                    }
                    onMouseLeave={() =>
                      setIsPictureInPictureCircleHovered(false)
                    }
                    sx={{
                      width: 36,
                      height: 36,
                      ...(showLiveMonitoringFloorControls && {
                        bgcolor: "rgba(0, 0, 0, 0.55)",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.75)" },
                      }),
                    }}
                    title="Close Floor Plan"
                  >
                    <PictureInPictureCircleIcon
                      sx={{ fontSize: 24, color: "#fff" }}
                      fillColor={
                        isPictureInPictureCircleHovered ? "#1BC800" : "none"
                      }
                    />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setIsImageViewerMinimized(true);
                      setIsPictureInMaxPictureHovered(false);
                      setIsPictureInPictureHovered(false);
                    }}
                    onMouseEnter={() => setIsPictureInPictureHovered(true)}
                    onMouseLeave={() => setIsPictureInPictureHovered(false)}
                    sx={{
                      width: 36,
                      height: 36,
                      ...(showLiveMonitoringFloorControls && {
                        bgcolor: "rgba(0, 0, 0, 0.55)",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.75)" },
                      }),
                    }}
                    title="Minimize Floor Plan"
                  >
                    <PictureInPictureIcon
                      sx={{ fontSize: 24, color: "#fff" }}
                      fillColor={isPictureInPictureHovered ? "#1BC800" : "none"}
                    />
                  </IconButton>
                  {showLiveMonitoringFloorControls && (
                    <IconButton
                      onClick={handleToggleFullscreen}
                      sx={{
                        width: 36,
                        height: 36,
                        color: "#fff",
                        bgcolor: "rgba(0, 0, 0, 0.55)",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.75)" },
                      }}
                      title={
                        isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"
                      }
                    >
                      {isFullscreen ? (
                        <FullscreenExitIcon sx={{ fontSize: 22 }} />
                      ) : (
                        <FullscreenIcon sx={{ fontSize: 22 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  <PatrolImageViewer
                    imageUrl={imageUrl}
                    selectedPatrol={selectedPatrol}
                    onClose={() => setIsImageViewerMinimized(true)}
                    currentPinId={currentPinId}
                    isImageViewerMinimized={isImageViewerMinimized}
                    yaw={rotation.yaw}
                    fov={rotation.fov}
                    rotationCycleComplete={isRotationCycleComplete}
                    slideDirection={slideDirection}
                    onSlideComplete={handleSlideComplete}
                    onPinTransitionComplete={handlePinTransitionComplete}
                    onPatrolSlideAborted={handlePatrolFloorSlideAborted}
                    onPatrolFloorSlideStart={handlePatrolFloorSlideStart}
                    enableZoom={!isImageViewerMinimized}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
